import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import DynamicTable from '../components/common/DynamicTable';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItem,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Science as ScienceIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Share as ShareIcon,
  AutoAwesome as AutoAwesomeIcon,
  Category as CategoryIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { fetchWithAuth, loadStateFromDatabase, syncStateToDatabase } from "../utils/auth";
import { jwtDecode } from 'jwt-decode';
import ArticleAnnotation from '../components/features/ArticleAnnotation';
import ResearchInsights from '../components/features/ResearchInsights';
import CategorySidebar from '../components/features/CategorySidebar';
import CategorySelector from '../components/common/CategorySelector';
import { useInteractiveTutorial } from '../utils/InteractiveTutorialContext';
import { getButtonStyles, BUTTON_COLORS } from '../config/buttonStyles';
import { useTheme } from '../utils/ThemeContext';
import { CATEGORY_COLORS, getThemeAwareColor, getCategoryStyles } from '../utils/categoryColors';
import '../assets/css/futuristic-modern.css';

import axios from 'axios';
import API_BASE_URL from '../config/api';

function HomePage() {
  const { 
    validateStepAction, 
    completeStep, 
    isActive: isTutorialActive, 
    getCurrentStep, 
    currentStepIndex,
    isFirstLogin,
    tutorialStatusLoaded,
    startTutorial,
    checkUnlockConditions
  } = useInteractiveTutorial();
  const { isDarkMode } = useTheme();
  const [keyword, setKeyword] = useState('');
  const [dropdownValue, setDropdownValue] = useState('and');
  const [query, setQuery] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [availableDomains, setAvailableDomains] = useState([]);
  const [createDomainOpen, setCreateDomainOpen] = useState(false);
  const [newDomainName, setNewDomainName] = useState('');
  const [newDomainDescription, setNewDomainDescription] = useState('');
  const [domainCreationLoading, setDomainCreationLoading] = useState(false);
  const [deleteDomainOpen, setDeleteDomainOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState(null);
  const [data, setData] = useState(null);
  const [articlesData, setArticlesData] = useState(null);
  const [originalArticlesData, setOriginalArticlesData] = useState(null); // Keep original unfiltered data
  const [loading, setLoading] = useState(false);
  const [manualReadStatusChange, setManualReadStatusChange] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState([]);
  const [aiFilteringSummaries, setAiFilteringSummaries] = useState({});
  const [originalAiFilteringSummaries, setOriginalAiFilteringSummaries] = useState({});
  const [dataCacheKey, setDataCacheKey] = useState(0); // Cache invalidation key
  const [openModifyDialog, setOpenModifyDialog] = useState(false);
  const [criteriaToModify, setCriteriaToModify] = useState({
    criteria: [''],
    start_date: [''],
    end_date: [''],
    domain: ['']
  });
  const [deleteQueryDialogOpen, setDeleteQueryDialogOpen] = useState(false);
  const [queryToDelete, setQueryToDelete] = useState(null);
    // State persistence keys - User-specific to prevent cross-user data leakage
  const getUserStateKey = () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;
      
      // Decode JWT to get user email
      const decoded = jwtDecode(token);
      const userEmail = decoded.sub || null;
      
      if (!userEmail) return null;
      
      // Return user-specific key
      return `aletheon_homepage_state_${userEmail}`;
    } catch (error) {
      console.error('Error getting user state key:', error);
      return null;
    }
  };
  
  const STATE_KEYS = {
    get HOMEPAGE_STATE() {
      return getUserStateKey() || 'aletheon_homepage_state'; // Fallback for safety
    }
  };
  
  // Helper function to safely load state only for authenticated user
  const loadSavedState = () => {
    const stateKey = getUserStateKey();
    if (!stateKey) {
      return null; // No valid user, no state to load
    }
    
    try {
      const savedState = localStorage.getItem(stateKey);
      return savedState ? JSON.parse(savedState) : null;
    } catch (e) {
      console.error('Error loading saved state:', e);
      return null;
    }
  };
  
  const [indexToModify, setIndexToModify] = useState('');
  
  // State for managing the active tab - INITIALIZE FROM SAVED STATE
  const [activeTab, setActiveTab] = useState(() => {
    const savedState = loadSavedState();
    return savedState?.activeTab || 'queries';
  });
  
  // State for managing expanded accordions - INITIALIZE FROM SAVED STATE
  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    const savedState = loadSavedState();
    return savedState?.expandedAccordions || {};
  });
  
  // State for managing accordion articles
  const [accordionArticles, setAccordionArticles] = useState({});
  
  // State for managing hidden "no" articles per query
  const [hiddenNoArticles, setHiddenNoArticles] = useState(() => {
    const savedState = loadSavedState();
    return savedState?.hiddenNoArticles || {};
  });
  
  // Removed tableRenderKeys state - no longer needed as it caused column sliding
  
  // Snackbar notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showNotification = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Category management state
  const [categories, setCategories] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState([]); // Array for multi-select
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [articleToCategorize, setArticleToCategorize] = useState(null);
  const [showCategories, setShowCategories] = useState(false); // Toggled off by default
  const [createCategoryDialogOpen, setCreateCategoryDialogOpen] = useState(false);
  const [newCategoryForm, setNewCategoryForm] = useState({
    name: '',
    color: CATEGORY_COLORS[0] // Use first color from new palette
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };
  
  // State for managing open articles - INITIALIZE FROM SAVED STATE
  const [openArticles, setOpenArticles] = useState(() => {
    const savedState = loadSavedState();
    return savedState?.openArticles || [];
  });
  
  // State for managing active article tab - INITIALIZE FROM SAVED STATE
  const [activeArticleTab, setActiveArticleTab] = useState(() => {
    const savedState = loadSavedState();
    return savedState?.activeArticleTab || 'queries-tab';
  });
  
  // Sharing states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [lastSharedUser, setLastSharedUser] = useState(() => {
    // Load last shared user from localStorage
    try {
      const saved = localStorage.getItem('lastSharedUser');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading last shared user:', error);
      return null;
    }
  });
  
  // State for managing selected query index - INITIALIZE FROM SAVED STATE
  const [selectedQueryIndex, setSelectedQueryIndex] = useState(() => {
    const savedState = loadSavedState();
    return savedState?.selectedQueryIndex !== undefined ? savedState.selectedQueryIndex : 0;
  });
  
  // State for managing table pagination - INITIALIZE FROM SAVED STATE
  const [tablePagination, setTablePagination] = useState(() => {
    const savedState = loadSavedState();
    return savedState?.tablePagination || { 0: 0 }; // Default: first page for each query
  });
  
  // Permanent queries tab ID
  const QUERIES_TAB_ID = 'queries-tab';
  
  // State persistence system
  
  // Helper to get auth token
  const getAuthToken = () => localStorage.getItem('authToken');

  // State persistence functions
  // Save state to localStorage (fast, for page refreshes)
  const saveHomepageState = useCallback(() => {
    // Get user-specific key
    const stateKey = getUserStateKey();
    if (!stateKey) {
      return; // No valid user, don't save
    }
    
    const stateToSave = {
      activeTab,
      expandedAccordions,
      openArticles,
      activeArticleTab,
      hiddenNoArticles,
      
      // Save additional session state
      selectedQueryIndex: selectedQueryIndex || 0,
      tablePagination: tablePagination || { 0: 0 },
      timestamp: Date.now()
    };
    
      try {
        localStorage.setItem(stateKey, JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Error saving homepage state:', error);
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, expandedAccordions, openArticles, activeArticleTab, selectedQueryIndex, tablePagination]);

  // Save state to database (for logout/long-term persistence)
  const saveHomepageStateToDatabase = useCallback(async () => {
    try {
      const stateToSave = {
        activeTab,
        expandedAccordions,
        openArticles,
        activeArticleTab,
        hiddenNoArticles,
        selectedQueryIndex: selectedQueryIndex || 0,
        tablePagination: tablePagination || { 0: 0 }
      };

      await fetchWithAuth('/api/user-state/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          homepage_state: stateToSave
        })
      });
    } catch (error) {
      console.error('Error saving homepage state to database:', error);
    }
  }, [activeTab, expandedAccordions, openArticles, activeArticleTab, hiddenNoArticles, selectedQueryIndex, tablePagination]);

  // Expose saveToDatabase globally for SignOut to use
  useEffect(() => {
    window.saveHomepageStateToDatabase = saveHomepageStateToDatabase;
    return () => {
      delete window.saveHomepageStateToDatabase;
    };
  }, [saveHomepageStateToDatabase]);

  // Load state from database and restore
  const restoreHomepageState = useCallback(async () => {
    try {
      const stateKey = getUserStateKey();
      if (!stateKey) {
        return false;
      }
      
      // First, try localStorage for fast restore
      const savedState = localStorage.getItem(stateKey);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        const now = Date.now();
        const stateAge = now - parsedState.timestamp;
        
        // If localStorage is recent (< 5 min), use it
        if (stateAge < 300000) {
          applyState(parsedState);
          return true;
        }
      }
      
      // Check if token exists before trying database
      const token = localStorage.getItem('authToken');
      if (!token) {
        // No token, can't load from database, use localStorage if available
        if (savedState) {
          applyState(JSON.parse(savedState));
        }
        return false;
      }
      
      // Otherwise, load from database (after logout/login or stale localStorage)
      const response = await fetchWithAuth('/api/user-state/load');
      const data = await response.json();
      
      if (data.state && data.state.homepage_state) {
        const dbState = data.state.homepage_state;
        applyState(dbState);
        
        // Update localStorage with database state
        const stateWithTimestamp = { ...dbState, timestamp: Date.now() };
        localStorage.setItem(stateKey, JSON.stringify(stateWithTimestamp));
        return true;
      } else if (savedState) {
        // Database has no state, but localStorage does - use it anyway
        applyState(JSON.parse(savedState));
        return true;
      }
    } catch (error) {
      console.error('Error restoring homepage state:', error);
    }
    return false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to apply state
  const applyState = (state) => {
    if (state.activeTab) setActiveTab(state.activeTab);
    if (state.expandedAccordions) setExpandedAccordions(state.expandedAccordions);
    if (state.openArticles) setOpenArticles(state.openArticles);
    if (state.activeArticleTab) setActiveArticleTab(state.activeArticleTab);
    if (state.hiddenNoArticles) setHiddenNoArticles(state.hiddenNoArticles);
    if (state.selectedQueryIndex !== undefined) setSelectedQueryIndex(state.selectedQueryIndex);
    if (state.tablePagination) setTablePagination(state.tablePagination);
  };

  // Restore state on component mount (only if logged in and localStorage is stale)
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    
    const stateKey = getUserStateKey();
    if (!stateKey) return;
    
    // Check if localStorage has recent state
    const savedState = localStorage.getItem(stateKey);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        const stateAge = Date.now() - (parsedState.timestamp || 0);
        
        // If localStorage is recent (< 5 min), skip database load
        // State was already applied during initialization
        if (stateAge < 300000) {
          return;
        }
      } catch (e) {
        // Invalid state, continue to load from database
      }
    }
    
    // Only load from database if localStorage is stale or missing
    restoreHomepageState();
  }, [restoreHomepageState]);

  // eslint-disable-next-line no-unused-vars
  const clearHomepageState = useCallback(() => {
    try {
      const stateKey = getUserStateKey();
      if (stateKey) {
        localStorage.removeItem(stateKey);
        console.log('Homepage state cleared for user');
      }
    } catch (error) {
      console.error('Error clearing homepage state:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load state from database on mount (cross-device sync)
  useEffect(() => {
    const loadInitialState = async () => {
      try {
        // Try to load from database first (for cross-device sync)
        await loadStateFromDatabase();
        // State is automatically stored in localStorage by loadStateFromDatabase
        // Component state will be initialized from localStorage
      } catch (error) {
        console.error('Error loading state from database:', error);
        // Fallback to localStorage is automatic (component already initialized with it)
      }
    };
    
    loadInitialState();
  }, []); // Run once on mount

  // Auto-start tutorial on first login
  useEffect(() => {
    if (tutorialStatusLoaded && isFirstLogin && !isTutorialActive) {
      // Wait a short moment for the page to render, then auto-start
      const timer = setTimeout(() => {
        console.log('🎓 First login detected - auto-starting onboarding tutorial');
        startTutorial('research_portal_tutorial');
      }, 1000); // 1 second delay for smooth experience
      
      return () => clearTimeout(timer);
    }
  }, [tutorialStatusLoaded, isFirstLogin, isTutorialActive, startTutorial]);

  // Check unlock conditions when articles are loaded
  useEffect(() => {
    const hasAnyArticles =
      // Articles loaded into accordion state
      Object.values(accordionArticles || {}).some(arr => Array.isArray(arr) && arr.length > 0)
      // Or articles present in original data
      || (Array.isArray(originalArticlesData) && originalArticlesData.some(q => Array.isArray(q?.articles?.[0]) && q.articles[0].length > 0))
      // Or any open article tab exists
      || (Array.isArray(openArticles) && openArticles.length > 0);
    
    if (hasAnyArticles && checkUnlockConditions) {
      // User now has articles - check if AI Assistant and Collaboration should unlock
      checkUnlockConditions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accordionArticles, originalArticlesData, openArticles]);

  // Periodic sync to database (every 5 minutes)
  useEffect(() => {
    const syncInterval = setInterval(() => {
      syncStateToDatabase().catch(error => {
        console.error('Periodic sync error:', error);
      });
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(syncInterval);
  }, []);

  const handleDateConversion = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateQuery = useCallback(
    debounce((newQuery) => {
      setQuery(newQuery);
    }, 1),
    []
  );

  // Function to trigger AI filtering for all queries to generate summaries
  const triggerAIFilteringForAllQueries = async (articlesData) => {
    console.log('🚀 triggerAIFilteringForAllQueries called with:', articlesData?.length, 'queries');
    if (!articlesData || articlesData.length === 0) return;
    
    for (let i = 0; i < articlesData.length; i++) {
      const queryData = articlesData[i];
      const domain = queryData.domain?.[0] || '';
      const articles = queryData.articles?.[0] || [];
      
      console.log(`📊 Query ${i}: domain="${domain}", articles=${articles.length}`);
      
      if (articles.length > 0) {
        // Check if articles already have AI predictions
        const hasSavedPredictions = articles.some(article => article.ai_prediction !== undefined);
        
        if (hasSavedPredictions) {
          // Generate summary from existing predictions
          console.log(`✅ Query ${i}: Found existing AI predictions`);
          const summary = {
            selected: articles.filter(article => article.ai_prediction === 'yes').length,
            maybe: articles.filter(article => article.ai_prediction === 'maybe').length,
            notSelected: articles.filter(article => article.ai_prediction === 'no').length,
            total: articles.length
          };
          
          console.log(`📈 Query ${i}: Summary:`, summary);
          setAiFilteringSummaries(prev => {
            const newSummaries = { ...prev, [i]: summary };
            console.log(`💾 Setting aiFilteringSummaries for query ${i}:`, newSummaries);
            return newSummaries;
          });
          setOriginalAiFilteringSummaries(prev => {
            const newSummaries = { ...prev, [i]: summary };
            console.log(`💾 Setting originalAiFilteringSummaries for query ${i}:`, newSummaries);
            return newSummaries;
          });
        } else {
          // Run AI filtering for this query
          console.log(`🚀 Query ${i}: Running AI filtering for domain "${domain}"`);
          try {
            const response = await fetchWithAuth('/api/ai-filter-articles', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                articles: articles,
                domain: domain
              }),
            });

            if (response.ok) {
              const result = await response.json();
              if (result.success) {
                const summary = {
                  selected: result.filtered_articles.filter(article => article.prediction === 'yes').length,
                  maybe: result.filtered_articles.filter(article => article.prediction === 'maybe').length,
                  notSelected: result.filtered_articles.filter(article => article.prediction === 'no').length,
                  total: result.filtered_articles.length
                };
                
                setAiFilteringSummaries(prev => ({ ...prev, [i]: summary }));
                setOriginalAiFilteringSummaries(prev => ({ ...prev, [i]: summary }));
                
                // Hide "No" articles by default when model exists and has predictions
                setHiddenNoArticles(prev => ({ ...prev, [i]: true }));
                
                // Save AI predictions to database
                await fetchWithAuth('/api/save-ai-predictions', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    articles: result.filtered_articles.map(article => ({
                      query_index: i,
                      query_id: articlesData[i]?.articles?.[0]?.[0]?.unique_query_id,  // Pass unique ID for safety
                      article_index: article.article_id,
                      ai_prediction: article.prediction,
                      ai_confidence: article.confidence,
                      ai_explanation: article.explanation,
                      ai_reasoning: article.reasoning,
                      domain: domain
                    }))
                  }),
                });
              } else {
                // If filtering failed (e.g., no model), show all articles
                setHiddenNoArticles(prev => ({ ...prev, [i]: false }));
              }
            } else {
              // If request failed, show all articles
              setHiddenNoArticles(prev => ({ ...prev, [i]: false }));
            }
          } catch (error) {
            console.error(`Error running AI filtering for query ${i}:`, error);
            // If AI filtering fails (e.g., no model exists), show all articles by default
            setHiddenNoArticles(prev => ({ ...prev, [i]: false }));
          }
        }
      } else if (articles.length > 0 && !domain) {
        // Query without domain - just show article count
        console.log(`📊 Query ${i}: No domain, showing basic article count`);
        const summary = {
          selected: 0,
          maybe: 0,
          notSelected: 0,
          total: articles.length
        };
        
        setAiFilteringSummaries(prev => {
          const newSummaries = { ...prev, [i]: summary };
          console.log(`💾 Setting aiFilteringSummaries for query ${i} (no domain):`, newSummaries);
          return newSummaries;
        });
        setOriginalAiFilteringSummaries(prev => {
          const newSummaries = { ...prev, [i]: summary };
          console.log(`💾 Setting originalAiFilteringSummaries for query ${i} (no domain):`, newSummaries);
          return newSummaries;
        });
      }
    }
  };

  // 🛡️ PRODUCTION-SAFE: Wait for articles to actually appear in database
  // Instead of trusting is_fetching flag, verify articles exist
  const waitForArticlesInDatabase = async (queryId, maxAttempts = 30) => {
    return new Promise((resolve) => {
      let attempts = 0;
      
      const checkArticles = async () => {
        try {
          const response = await fetchWithAuth(`${API_BASE_URL}/api/get-searches`);
          if (!response.ok) {
            throw new Error('Failed to fetch searches');
          }
          
          const data = await response.json();
          const searches = data.metadata || [];  // Backend returns 'metadata' not 'searches'
          
          // Find our specific query by ID
          const query = searches.find(q => q.id === queryId);
          
          console.log(`🔄 Verifying articles in DB (attempt ${attempts + 1}/${maxAttempts}):`, {
            queryId,
            found: !!query,
            articleCount: query?.articles?.length || 0
          });
          
          if (query && query.articles && query.articles.length > 0) {
            console.log(`✅ Articles verified in database: ${query.articles.length} articles`);
            resolve({ success: true, articleCount: query.articles.length });
            return;
          }
          
          attempts++;
          if (attempts >= maxAttempts) {
            console.log('⏱️ Verification timeout: No articles found in database');
            resolve({ success: false, articleCount: 0 });
            return;
          }
          
          // Retry in 1 second
          setTimeout(checkArticles, 1000);
        } catch (error) {
          console.error('❌ Error verifying articles:', error);
          attempts++;
          
          if (attempts >= maxAttempts) {
            console.log('⏱️ Verification failed after max attempts');
            resolve({ success: false, error: error.message });
            return;
          }
          
          // Retry in 1 second even on error
          setTimeout(checkArticles, 1000);
        }
      };
      
      // Start checking
      checkArticles();
    });
  };

  // Separate function to refresh data without loading guards
  const refreshSearchData = async () => {
    try {
      console.log('🔄 Refreshing search data after background fetch...');
      
      const response = await axios.get(`${API_BASE_URL}/api/get-searches`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        }
      });
      const { metadata } = response.data;
      
      // Count new articles before and after
      const oldArticleCount = articlesData ? articlesData.reduce((sum, q) => sum + (q.articles[0]?.length || 0), 0) : 0;
      
      const newCriteriaArray = metadata.map((metadata_, index) => ({
        criteria: [metadata_['search_criteria']],
        start_date: [metadata_['start_date']],
        end_date: [metadata_['end_date']],
        domain: [metadata_['domain'] || ''],
        unique_query_id: metadata_['id']  // Add unique ID for safe backend operations
      }));
      
      setSearchCriteria([...newCriteriaArray]);

      const articlesData_ = metadata.map((metadata_, index) => ({
        criteria: [metadata_['search_criteria']],
        start_date: [metadata_['start_date']],
        end_date: [metadata_['end_date']],
        domain: [metadata_['domain'] || ''],
        articles: [metadata_['articles'].map((article, articleIdx) => ({
          ...article,
          query_id: index,
          article_index: articleIdx
        }))]
      }));

      const newArticleCount = articlesData_.reduce((sum, q) => sum + (q.articles[0]?.length || 0), 0);
      const addedArticles = newArticleCount - oldArticleCount;

      setArticlesData(articlesData_);
      setOriginalArticlesData(articlesData_); // Save original unfiltered data
      
      // Force refresh ALL accordion articles (expanded or not) to show newly fetched data
      const newAccordionArticles = {};
      for (let index = 0; index < articlesData_.length; index++) {
        if (articlesData_[index] && articlesData_[index].articles && articlesData_[index].articles[0]) {
          const articles = articlesData_[index].articles[0];
          const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
          const formattedArticles = articles.map((article, articleIndex) => ({
            id: toRowId(article),
            ...article,
            query_id: article.query_id !== undefined ? article.query_id : index,
            unique_query_id: article.unique_query_id,  // Pass through unique ID
            query_index: article.query_index !== undefined ? article.query_index : index,
            article_index: article.article_index !== undefined ? article.article_index : articleIndex,
            read: article.read || false,
            read_date: article.read_date || null,
          }));
          
          // Only set if accordion is expanded OR if this is a new query with articles
          if (expandedAccordions[index] || formattedArticles.length > 0) {
            newAccordionArticles[index] = formattedArticles;
          }
        }
      }
      
      // Update all accordion articles at once
      setAccordionArticles(prev => ({
        ...prev,
        ...newAccordionArticles
      }));
      
      if (addedArticles > 0) {
        console.log(`✅ Data refreshed! Added ${addedArticles} new articles!`);
        showNotification(`✅ ${addedArticles} articles fetched successfully!`, "success");
      } else {
        console.log('✅ Data refreshed! No new articles found.');
        showNotification("✅ Query updated successfully!", "success");
      }
      
      // Re-trigger AI filtering for updated queries
      setTimeout(() => {
        triggerAIFilteringForAllQueries(articlesData_);
      }, 100);
    } catch (error) {
      console.error('❌ Error refreshing search data:', error);
    }
  };

  const fetchSearchCriteria = async (forceRefresh = false, skipPolling = false) => {
    try {
      if (!loading || forceRefresh){
        setLoading(true);
        
        const response = await fetchWithAuth('/api/get-searches');
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API request failed with status ${response.status}: ${errorText}`);
        }
        
        const responseData = await response.json();
        const { metadata, background_fetch_triggered, is_fetching } = responseData;
        
        const newCriteriaArray = metadata.map((metadata_, index) => ({
          criteria: [metadata_['search_criteria']],
          start_date: [metadata_['start_date']],
          end_date: [metadata_['end_date']],
          domain: [metadata_['domain'] || ''],
          unique_query_id: metadata_['id']  // Add unique ID for safe backend operations
        }));
        
        setSearchCriteria(newCriteriaArray);
  
        const articlesData_ = metadata.map((metadata_, index) => ({
          criteria: [metadata_['search_criteria']],
          start_date: [metadata_['start_date']],
          end_date: [metadata_['end_date']],
          domain: [metadata_['domain'] || ''],
          articles: [metadata_['articles'].map((article, articleIdx) => ({
            ...article,
            query_id: index,  // Numeric index (legacy)
            unique_query_id: metadata_['id'],  // Unique ID (new, safe)
            article_index: articleIdx
          }))]
        }));
  
        setArticlesData(articlesData_);
        setOriginalArticlesData(articlesData_);
        setDataFetched(true);
        
        // Populate accordion articles so queries are actually displayed
        const newAccordionArticles = {};
        for (let index = 0; index < articlesData_.length; index++) {
          if (articlesData_[index] && articlesData_[index].articles && articlesData_[index].articles[0]) {
            const articles = articlesData_[index].articles[0];
            const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
            const formattedArticles = articles.map((article, articleIndex) => ({
              id: toRowId(article),
              ...article,
              query_id: article.query_id !== undefined ? article.query_id : index,
              query_index: article.query_index !== undefined ? article.query_index : index,
              article_index: article.article_index !== undefined ? article.article_index : articleIndex,
              read: article.read || false,
              read_date: article.read_date || null,
            }));
            
            if (formattedArticles.length > 0) {
              newAccordionArticles[index] = formattedArticles;
            }
          }
        }
        
        setAccordionArticles(prev => ({
          ...prev,
          ...newAccordionArticles
        }));
        
        // Clear loading based on fetch status and data availability
        if (skipPolling) {
          setLoading(false);
        } else {
          const hasArticles = articlesData_.some(q => q.articles?.[0]?.length > 0);
          
          if ((background_fetch_triggered || is_fetching) && !hasArticles) {
            // Keep loading only if fetch is in progress and no articles yet
          } else {
            setLoading(false);
          }
        }
        
        // Trigger AI filtering for all queries to generate summaries
        setTimeout(() => {
          triggerAIFilteringForAllQueries(articlesData_);
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching search criteria:', error);
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (!dataFetched) {
      fetchSearchCriteria();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFetched]);

  // Auto-refresh disabled - caused jarring UX during active use
  // Users can manually refresh if needed with the refresh button
  // useEffect(() => {
  //   if (!getAuthToken() || !dataFetched) return;
  //
  //   const articlesRefreshInterval = setInterval(() => {
  //     if (!document.hidden) { // Only refresh when tab is visible
  //       fetchSearchCriteria(true, true); // Force refresh, skip polling
  //     }
  //   }, 300000); // 5 minutes (300 seconds)
  //   
  //   return () => clearInterval(articlesRefreshInterval);
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [dataFetched]);
  
  // Fetch available domains on mount
  useEffect(() => {
    const fetchAvailableDomains = async () => {
      try {
        const response = await fetchWithAuth('/api/domain/available-domains');
        
        if (response.ok) {
          const data = await response.json();
          setAvailableDomains(data.domains || []);
        } else {
          console.error('Failed to fetch available domains:', response.status);
          setAvailableDomains([]);
        }
      } catch (error) {
        console.error('Error fetching available domains:', error);
        setAvailableDomains([]);
      }
    };
    
    fetchAvailableDomains();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Tutorial: auto-validation removed; user advances via Continue only

  // Tutorial tab switching logic - Smart tab switching based on current step
  useEffect(() => {
    if (isTutorialActive) {
      const currentStep = getCurrentStep();
      
      if (currentStep) {
        const stepId = currentStep.id;
        
            // Steps that need queries tab with query table visible
            const queryTableSteps = ['open_article', 'assign_category', 'category_annotation', 'change_prediction'];
            
            // Steps that need queries tab with article detail visible
            const articleDetailSteps = ['highlight_text', 'click_highlight_button', 'add_comment_to_text', 'click_comment_button'];
            
            // Steps that need categories panel open
            const categoryPanelSteps = ['open_categories_panel', 'create_category', 'category_name_input', 'category_color_picker', 'category_save'];
            const categoryCloseSteps = ['assign_category', 'category_annotation'];
        
        if (queryTableSteps.includes(stepId)) {
          setActiveTab('queries');
          setActiveArticleTab('queries-tab');
        } else if (articleDetailSteps.includes(stepId)) {
          setActiveTab('queries');
          // Force article tab to be open if there are open articles
          if (openArticles.length > 0) {
            setActiveArticleTab(openArticles[openArticles.length - 1].tabId);
          }
        } else if (categoryPanelSteps.includes(stepId)) {
          setActiveTab('queries');
          setActiveArticleTab('queries-tab');
        } else if (categoryCloseSteps.includes(stepId)) {
          setActiveTab('queries');
          setActiveArticleTab('queries-tab');
          setShowCategories(false);
        }
      }
    }
  }, [isTutorialActive, currentStepIndex, activeTab, activeArticleTab, openArticles, getCurrentStep]);

  // Listen for tutorial step changes via custom event
  useEffect(() => {
    const handleTutorialStepChange = (event) => {
      const { stepId } = event.detail;
      
            // Steps that need queries tab with query table visible
            const queryTableSteps = ['open_article', 'assign_category', 'category_annotation', 'change_prediction'];
            
            // Steps that need queries tab with article detail visible
            const articleDetailSteps = ['highlight_text', 'click_highlight_button', 'add_comment_to_text', 'click_comment_button'];
            
            // Steps that need categories panel open
            const categoryPanelSteps = ['open_categories_panel', 'create_category', 'category_name_input', 'category_color_picker', 'category_save'];
            const categoryCloseStepsLocal = ['assign_category', 'category_annotation'];
      
      if (queryTableSteps.includes(stepId)) {
        // Show query table
        setActiveTab('queries');
        setActiveArticleTab('queries-tab');
      } else if (articleDetailSteps.includes(stepId)) {
        // Show article detail (force the open article tab active)
        setActiveTab('queries');
        if (openArticles.length > 0) {
          setActiveArticleTab(openArticles[openArticles.length - 1].tabId);
        }
      } else if (categoryPanelSteps.includes(stepId)) {
        setActiveTab('queries');
        setActiveArticleTab('queries-tab');
      } else if (categoryCloseStepsLocal.includes(stepId)) {
        setActiveTab('queries');
        setActiveArticleTab('queries-tab');
        setShowCategories(false);
      }
    };

    window.addEventListener('tutorialStepChange', handleTutorialStepChange);
    return () => window.removeEventListener('tutorialStepChange', handleTutorialStepChange);
  }, [openArticles]);
  
  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchWithAuth(`/api/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data.categories || []);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    
    loadCategories();
  }, []); // Run once on mount

  // Handle category selection from sidebar (multi-select with toggle)
  const handleSelectCategory = async (categoryId) => {
    // Toggle category selection (multi-select)
    let newSelection;
    if (categoryId === null) {
      // "All Articles" - clear all selections
      newSelection = [];
    } else if (selectedCategoryFilter.includes(categoryId)) {
      // Category already selected - unselect it
      newSelection = selectedCategoryFilter.filter(id => id !== categoryId);
    } else {
      // Add category to selection
      newSelection = [...selectedCategoryFilter, categoryId];
    }
    
    setSelectedCategoryFilter(newSelection);
    
    if (newSelection.length === 0) {
      // Show all articles - restore original data
      if (originalArticlesData) {
        setArticlesData(originalArticlesData);
        
        // Clear filtered accordion data - will be repopulated on expand
        // Keep only accordions that were already populated in the original view
        setAccordionArticles(prev => {
          const restored = {};
          Object.keys(prev).forEach(key => {
            const idx = parseInt(key);
            // Only keep if this query index exists in original data
            if (idx < originalArticlesData.length && originalArticlesData[idx]) {
              const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
              restored[idx] = originalArticlesData[idx].articles[0].map(article => ({
                id: toRowId(article),
                ...article,
                read: article.read || false,
                read_date: article.read_date || null,
              }));
            }
          });
          return restored;
        });
      } else {
        console.error('❌ originalArticlesData is null!');
      }
    } else {
      // Filter original data to show articles matching ANY selected category
      if (!originalArticlesData) {
        console.error('❌ Original data not available');
        return;
      }
      
      // Filter each query to show articles matching ANY of the selected categories
      // IMPORTANT: Keep ALL queries to preserve original table structure!
      const filteredDataWithIndices = originalArticlesData.map((query, queryIdx) => {
        const allArticles = query.articles[0] || [];
        const filteredArticles = allArticles.filter(article => {
          // Check if article matches ANY selected category (OR logic)
          return newSelection.some(selectedId => {
            if (selectedId === 'uncategorized') {
              return article.category_id === null || article.category_id === undefined;
            }
            return article.category_id === selectedId;
          });
        });
        
        // Return query with filtered articles - keep original index as queryIdx
        return {
          ...query,
          articles: [filteredArticles],
          originalQueryIndex: queryIdx  // Track original index!
        };
      }); // Keep ALL queries, even empty ones, to preserve table structure
      
      setArticlesData(filteredDataWithIndices);
      
      // Update accordionArticles AND expandedAccordions using ORIGINAL indices
      const newAccordionArticles = {};
      const newExpandedAccordions = {};
      
      filteredDataWithIndices.forEach((query, newIdx) => {
        const originalIdx = query.originalQueryIndex;
        const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
        
        // Populate accordion data using ORIGINAL index (to preserve table positions)
        newAccordionArticles[originalIdx] = query.articles[0].map(article => ({
          id: toRowId(article),
          ...article,
          read: article.read || false,
          read_date: article.read_date || null,
        }));
        
        // Preserve expansion state from ORIGINAL index
        if (expandedAccordions[originalIdx]) {
          newExpandedAccordions[originalIdx] = true;
        }
      });
      
      setAccordionArticles(newAccordionArticles);
      setExpandedAccordions(newExpandedAccordions);
    }
  };

  // Open category dialog for an article
  const handleOpenCategoryDialog = (article) => {
    setArticleToCategorize(article);
    setCategoryDialogOpen(true);
    // Tutorial step completion moved to CategorySelector component
  };

  // Close category dialog
  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setArticleToCategorize(null);
  };

  // Handle direct category change from dropdown (new design)
  const handleCategoryChange = async (article, categoryId) => {
    // Call the same handler as the dialog would
    await handleCategoryAssigned(article, categoryId);
  };

  // Handle category assigned to article
  const handleCategoryAssigned = async (article, categoryId) => {
    // IMMEDIATELY update local state for instant UI feedback
    const updatedAt = categoryId ? new Date().toISOString() : null;
    
    // 1. Update ORIGINAL data (so "All Articles" has the new category)
    setOriginalArticlesData(prev => {
      if (!prev) return prev;
      
      return prev.map((query, qIdx) => {
        if (qIdx !== article.query_id) return query;
        if (!query.articles || !query.articles[0]) return query;
        
        const updatedArticles = query.articles[0].map(a => 
          a.article_index === article.article_index
            ? { ...a, category_id: categoryId, categorized_at: updatedAt }
            : a
        );
        
        return {
          ...query,
          articles: [updatedArticles]
        };
      });
    });
    
    // 2. Update accordionArticles (table data)
    setAccordionArticles(prev => {
      const updated = { ...prev };
      // Find the article in all accordion indices
      for (const queryIndex in updated) {
        if (updated[queryIndex]) {
          updated[queryIndex] = updated[queryIndex].map(a => 
            (a.query_id === article.query_id && a.article_index === article.article_index)
              ? { ...a, category_id: categoryId, categorized_at: updatedAt }
              : a
          );
        }
      }
      return updated;
    });

    // 3. Update articlesData (current view - filtered or not)
    setArticlesData(prev => {
      if (!prev) return prev;
      
      return prev.map((query, qIdx) => {
        if (!query.articles || !query.articles[0]) return query;
        
        const updatedArticles = query.articles[0].map(a => {
          const isMatch = (a.query_id === article.query_id && a.article_index === article.article_index);
          return isMatch
            ? { ...a, category_id: categoryId, categorized_at: updatedAt }
            : a;
        });
        
        return {
          ...query,
          articles: [updatedArticles]
        };
      });
    });
    
    // 4. If we're in category-filtered view, re-filter to update the view
    if (selectedCategoryFilter.length > 0) {
      setOriginalArticlesData(currentOriginalData => {
        const filteredDataWithIndices = currentOriginalData.map((query, queryIdx) => {
          const allArticles = query.articles[0] || [];
          const filteredArticles = allArticles.filter(article => {
            return selectedCategoryFilter.some(selectedId => {
              if (selectedId === 'uncategorized') {
                return article.category_id === null || article.category_id === undefined;
              }
              return article.category_id === selectedId;
            });
          });
          
          return {
            ...query,
            articles: [filteredArticles],
            originalQueryIndex: queryIdx
          };
        });
        
        setArticlesData(filteredDataWithIndices);
        
        setExpandedAccordions(currentExpanded => {
          const newAccordionArticles = {};
          const newExpandedAccordions = {};
          
          filteredDataWithIndices.forEach((query, newIdx) => {
            const originalIdx = query.originalQueryIndex;
            const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
            
            newAccordionArticles[originalIdx] = query.articles[0].map(article => ({
              id: toRowId(article),
              ...article,
              read: article.read || false,
              read_date: article.read_date || null,
            }));
            
            if (currentExpanded[originalIdx]) {
              newExpandedAccordions[originalIdx] = true;
            }
          });
          
          setAccordionArticles(newAccordionArticles);
          return newExpandedAccordions;
        });
        
        return currentOriginalData;
      });
    }
    
    // 5. 🎯 NEW: Save category change to backend and JSONL file
    try {
      const response = await fetchWithAuth('/api/articles/categorize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          unique_query_id: article.unique_query_id,  // Unique ID for safety
          query_id: article.query_id,  // Index for legacy fallback
          article_index: article.article_index,
          category_id: categoryId
        })
      });

      if (response.ok) {
        console.log('✅ Category change saved to backend and JSONL file');
      } else {
        console.error('❌ Failed to save category change:', response.statusText);
        showNotification('Failed to save category change', 'error');
        return; // Don't show success notification if backend save failed
      }
    } catch (error) {
      console.error('❌ Error saving category change:', error);
      showNotification('Failed to save category change', 'error');
      return; // Don't show success notification if backend save failed
    }
    
    // 6. Reload categories to update counts (background, non-blocking)
    try {
      const response = await fetchWithAuth(`/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error reloading categories:', error);
    }
    
    showNotification('Article categorized successfully!', 'success');
    
    // Complete tutorial step if active
    try {
      if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'save_category_assignment') {
        completeStep('save_category_assignment');
      }
    } catch (_) {}
  };

  // Open dialog to create category
  const handleCreateCategoryFromSidebar = () => {
    setNewCategoryForm({
      name: '',
      color: '#6c3082'
    });
    setCreateCategoryDialogOpen(true);
    // Complete tutorial step: create category button clicked
    try {
      if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'create_category') {
        completeStep('create_category');
      }
    } catch (_) {}
  };

  const handleSaveNewCategory = async () => {
    try {
      if (!newCategoryForm.name.trim()) {
        showNotification('Category name is required', 'error');
        return;
      }

      const isEdit = !!newCategoryForm.id;
      const url = isEdit ? `/api/categories/${newCategoryForm.id}` : `/api/categories`;
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newCategoryForm.name,
          color: newCategoryForm.color
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to ${isEdit ? 'update' : 'create'} category`);
      }

      const data = await response.json();
      
      // Reload categories
      const categoriesResponse = await fetchWithAuth(`/api/categories`);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData.categories || []);
      }

      setCreateCategoryDialogOpen(false);
      // Close categories panel after creating to proceed with next tutorial steps
      setShowCategories(false);
      setNewCategoryForm({ name: '', color: CATEGORY_COLORS[0] });
      showNotification(`Category ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
      // Complete tutorial step: category saved
      try {
        if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'category_save') {
          completeStep('category_save');
        }
      } catch (_) {}
    } catch (error) {
      console.error('Error saving category:', error);
      showNotification(error.message || 'Failed to save category', 'error');
    }
  };

  // Handle edit category
  const handleEditCategory = (category) => {
    setNewCategoryForm({
      id: category.id,
      name: category.name,
      color: category.color
    });
    setCreateCategoryDialogOpen(true);
  };

  // Handle delete category
  const handleDeleteCategory = async (categoryId) => {
    try {
      const response = await fetchWithAuth(`/api/categories/${categoryId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete category');
      }

      // Remove from selection if selected
      if (selectedCategoryFilter.includes(categoryId)) {
        setSelectedCategoryFilter(prev => prev.filter(id => id !== categoryId));
      }

      // Reload categories and original data
      const categoriesResponse = await fetchWithAuth(`/api/categories`);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData.categories || []);
      }

      // Reload data to reflect uncategorized articles
      setDataCacheKey(prev => {
        const newKey = prev + 1;
        console.log(`🔄 Cache invalidated, new key: ${newKey}`);
        return newKey;
      }); // Invalidate cache
      await fetchSearchCriteria(true);

      showNotification('Category deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting category:', error);
      showNotification(error.message || 'Failed to delete category', 'error');
    }
  };

  // Calculate uncategorized articles count
  const calculateUncategorizedCount = () => {
    if (!articlesData) return 0;
    
    let count = 0;
    articlesData.forEach(query => {
      if (query.articles && query.articles[0]) {
        query.articles[0].forEach(article => {
          if (!article.category_id) {
            count++;
          }
        });
      }
    });
    return count;
  };

  // Calculate total articles count
  const calculateTotalArticles = () => {
    if (!articlesData) return 0;
    
    let count = 0;
    articlesData.forEach(query => {
      if (query.articles && query.articles[0]) {
        count += query.articles[0].length || 0;
      }
    });
    return count;
  };
  
  // Handle domain creation
  const handleCreateDomain = async () => {
    if (!newDomainName.trim()) {
      showNotification('Domain name is required', 'error');
      return;
    }
    
    setDomainCreationLoading(true);
    
    try {
      // Generate domain ID from name (lowercase, underscores)
      const domainId = newDomainName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      
      const response = await fetch(`${API_BASE_URL}/api/domains/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          id: domainId,
          name: newDomainName.trim(),
          description: newDomainDescription.trim()
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showNotification(`Domain "${newDomainName}" created successfully!`, 'success');
        
        const createdDomainId = domainId;
        
        // Close modal and reset form
        setCreateDomainOpen(false);
        setNewDomainName('');
        setNewDomainDescription('');
        
        // Refresh available domains to include the new one
        const domainsResponse = await fetchWithAuth(`/api/domain/available-domains?t=${Date.now()}`);
        
        if (domainsResponse.ok) {
          const domainsData = await domainsResponse.json();
          
          // Always add the new domain manually to ensure it appears
          const newDomain = {
            key: createdDomainId,
            name: newDomainName.trim(),
            is_custom: true,
            annotation_count: 0,
            has_model: false
          };
          
          // Check if domain already exists in the response
          const existingDomain = domainsData.domains?.find(d => d.key === createdDomainId);
          
          if (existingDomain) {
            // Domain is already in the list from backend
          setAvailableDomains(domainsData.domains || []);
          } else {
            // Manually add the domain to the list
            setAvailableDomains([...(domainsData.domains || []), newDomain]);
          }
          
          // Auto-select the newly created domain
          setSelectedDomain(createdDomainId);
        } else {
          // If fetching domains fails, still add the domain manually
          const newDomain = {
            key: createdDomainId,
            name: newDomainName.trim(),
            is_custom: true,
            annotation_count: 0,
            has_model: false
          };
          setAvailableDomains(prev => [...prev, newDomain]);
          setSelectedDomain(createdDomainId);
        }
      } else {
        showNotification(data.error || 'Failed to create domain', 'error');
      }
    } catch (error) {
      console.error('Error creating domain:', error);
      showNotification('Error creating domain', 'error');
    } finally {
      setDomainCreationLoading(false);
    }
  };
  
  // Open delete confirmation dialog
  const handleDeleteDomainClick = (domainId) => {
    const domain = availableDomains.find(d => d.key === domainId);
    setDomainToDelete(domain);
    setDeleteDomainOpen(true);
  };
  
  // Handle domain deletion
  const handleConfirmDeleteDomain = async () => {
    if (!domainToDelete) return;
    
    const domainId = domainToDelete.key;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/domains/${domainId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showNotification(`Domain "${domainToDelete.name}" deleted successfully!`, 'success');
        
        // If deleted domain was selected, clear selection
        if (selectedDomain === domainId) {
          setSelectedDomain('');
        }
        
        // Refresh available domains
        const domainsResponse = await fetch(`${API_BASE_URL}/api/domain/available-domains`, {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        });
        
        if (domainsResponse.ok) {
          const domainsData = await domainsResponse.json();
          setAvailableDomains(domainsData.domains || []);
        }
        
        // Close dialog
        setDeleteDomainOpen(false);
        setDomainToDelete(null);
      } else {
        showNotification(data.error || 'Failed to delete domain', 'error');
      }
    } catch (error) {
      console.error('Error deleting domain:', error);
      showNotification('Error deleting domain', 'error');
    }
  };
  
  // Save state whenever relevant state changes
  useEffect(() => {
    saveHomepageState();
  }, [activeTab, expandedAccordions, openArticles, activeArticleTab, hiddenNoArticles, tablePagination, saveHomepageState]);
  
  // Clear AI filtering summaries when data changes
  useEffect(() => {
    setAiFilteringSummaries({});
    setOriginalAiFilteringSummaries({});
  }, [searchCriteria]);
  
  
  // Simple state saving on specific state changes (no automatic user interaction detection)
  // This prevents the 1-second refresh issue while still maintaining state persistence
  

  

  

  
  // Auto-populate accordion articles when expanded state is restored - optimized
  useEffect(() => {
    if (articlesData && Object.keys(expandedAccordions).length > 0) {
      // Use setTimeout to defer this operation and prevent blocking render
      setTimeout(() => {
      Object.keys(expandedAccordions).forEach(index => {
        // ONLY populate if not already loaded (don't overwrite manual edits!)
        if (expandedAccordions[index] && !accordionArticles[index] && articlesData?.[index] && articlesData[index].articles && articlesData[index].articles[0]) {
          const articles = articlesData[index].articles[0];
          const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
          const formattedArticles = articles.map((article, articleIndex) => ({
            id: toRowId(article),  // USE STABLE ID!
            ...article,
            // Preserve backend-provided query_id/article_index if they exist (e.g., from category filter)
            // Otherwise use loop indices (for regular queries)
            query_id: article.query_id !== undefined ? article.query_id : parseInt(index),
            query_index: article.query_index !== undefined ? article.query_index : parseInt(index),
            article_index: article.article_index !== undefined ? article.article_index : articleIndex,
            read: article.read || false,
            read_date: article.read_date || null,
          }));
          
          setAccordionArticles(prev => ({ ...prev, [index]: formattedArticles }));
        }
      });
      }, 50); // Small delay to allow render to complete
    }
  }, [articlesData, expandedAccordions, accordionArticles]); // Added accordionArticles to deps
  
  // Update data state when accordionArticles changes and a query is selected
  useEffect(() => {
    if (accordionArticles && selectedQueryIndex !== null && accordionArticles[selectedQueryIndex] && !manualReadStatusChange) {
      const articles = accordionArticles[selectedQueryIndex];
      if (articles && articles.length > 0) {
        console.log('🔄 Updating data state with articles from database:', articles.length, 'articles');
        console.log('📊 First few articles read status:', articles.slice(0, 3).map(item => ({ id: item.id, read: item.read, title: item.title?.substring(0, 30) })));
        setData(articles);
      }
    } else if (manualReadStatusChange) {
      console.log('🚫 Skipping data update due to manual read status change flag');
    }
  }, [accordionArticles, selectedQueryIndex, manualReadStatusChange]);
  
  // Auto-restore table pagination when expanded state is restored (ONLY on initial load)
  // Removed tableRenderKeys logic to prevent forced re-renders that cause column sliding
  useEffect(() => {
    // Only run this when expandedAccordions changes (initial expansion), NOT when tablePagination changes
    if (Object.keys(expandedAccordions).length > 0) {
      // The pagination will be restored automatically by the DataGrid's currentPage prop
      // No need to force re-renders that cause column sliding
      console.log('📄 Pagination will be restored automatically by DataGrid props');
    }
  }, [expandedAccordions]); // ONLY run when expandedAccordions changes, NOT tablePagination
  

  

  
  


  


  const handleAddCriteria = async () => {
    // Prevent multiple submissions
    if (loading) {
      console.log('⏳ Already processing a query, ignoring duplicate click');
      return;
    }

    if (startDate == null) {
      showNotification("Start date cannot be empty!", "warning");
      return;
    }
    else {
      const newCriteria = {
        criteria: query,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate ? endDate.toISOString().split('T')[0] : null,
        domain: selectedDomain
      };
      // DON'T add to local state - let fetchSearchCriteria handle it after backend confirms
      try {
        setLoading(true); // Show loading indicator
        const requestData = {
            search_criteria: newCriteria.criteria,
            start_date: newCriteria.start_date,
            end_date: newCriteria.end_date,
            domain: newCriteria.domain
        };
        const response = await axios.post(`${API_BASE_URL}/api/add-search`, requestData, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });
        
        // 🛡️ SAFETY: Get verified query from backend response
        const createdQuery = response.data.query;
        const queryId = createdQuery?.id;
        
        console.log('✅ Query verified in database:', createdQuery);
        showNotification("Query added! Processing articles...", "info");
        
        // ✅ Display empty query immediately (verified from DB)
        await fetchSearchCriteria(true, true);
        
        // Complete tutorial step
        try {
          if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'fetch_button') {
            completeStep('fetch_button');
          }
        } catch (_) {}
        
        // Check if this unlocks AI Assistant tutorial
        if (checkUnlockConditions) {
          checkUnlockConditions();
        }
        
        // Now trigger article fetching
        try {
          console.log('🚀 Triggering article fetch for query:', newCriteria.criteria);
          const queryData = {
            query: newCriteria.criteria,
            startDate: newCriteria.start_date,
            endDate: newCriteria.end_date || new Date().toISOString().split('T')[0],
          };
          
          const processResponse = await fetchWithAuth('/process', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(queryData),
          });
          
          if (!processResponse.ok) {
            throw new Error(`HTTP error! status: ${processResponse.status}`);
          }
          
          const processData = await processResponse.json();
          console.log('✅ Fetch triggered, response:', processData);
          
          // 🛡️ PRODUCTION-SAFE: Wait for articles to actually appear in database
          console.log('⏳ Waiting for articles to be saved to database...');
          
          const verification = await waitForArticlesInDatabase(queryId, 30);
          
          if (verification.success) {
            console.log(`✅ ${verification.articleCount} articles verified in database`);
            showNotification(`${verification.articleCount} articles fetched successfully!`, 'success');
            
            // Final refresh to display articles
            await fetchSearchCriteria(true, true);
          } else {
            console.log('⏱️ Article verification timeout');
            showNotification('Fetch taking longer than expected. Please refresh page.', 'warning');
            
            // Still try to refresh
            await fetchSearchCriteria(true, true);
          }
          
          // Clear the form
          setQuery('');
          setStartDate(null);
          setEndDate(null);
          setSelectedDomain('');
          
        } catch (fetchError) {
          console.error('❌ Error fetching articles:', fetchError);
          showNotification('Query added but failed to fetch articles. Please try refreshing.', 'warning');
          await fetchSearchCriteria(true, true);
        } finally {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error adding search criteria:', error);
        showNotification("Error adding query: " + error.message, "error");
        setLoading(false);
      }
    }
  };

  const handleRemoveCriteria = (index) => {
    // Open confirmation dialog
    setQueryToDelete(index);
    setDeleteQueryDialogOpen(true);
  };
  
  const handleConfirmDeleteQuery = async () => {
    if (queryToDelete === null) return;
    
    try {
      // First delete from backend
      await axios.post(`${API_BASE_URL}/api/remove-search`, {
          index: queryToDelete
      }, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });
      
      // Clear local state immediately to prevent stale data
      setSearchCriteria([]);
      setArticlesData([]);
      setOriginalArticlesData([]);
      setDataFetched(false);
      setDataCacheKey(prev => prev + 1); // Invalidate cache
      
      // Close dialog
      setDeleteQueryDialogOpen(false);
      setQueryToDelete(null);
      
      // Then refresh from backend with force refresh
      await fetchSearchCriteria(true);
      showNotification('Query deleted successfully', 'success');
    } catch (error) {
        console.error('Error removing search criteria:', error);
        showNotification('Error deleting query', 'error');
        // Even if backend fails, refresh to get current state
        await fetchSearchCriteria(true);
    }
  };

  const handleModifyCriteria = (criteriaObj, index) => {
    setCriteriaToModify(criteriaObj);
    setIndexToModify(index);
    setOpenModifyDialog(true);
  };

  const handleSaveModifiedCriteria = async () => {
    const oldCriteria = searchCriteria[indexToModify];  
    try {
      const response = await axios.post(`${API_BASE_URL}/api/modify-search`, {
          query_to_remove: oldCriteria,
          query_to_modify: criteriaToModify
      }, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });
      fetchSearchCriteria();
      console.log('Search criteria modified:', response.data);
    } catch (error) {
        console.error('Error modifying search criteria:', error);
    }
    setOpenModifyDialog(false);
  };

  const addKeywordToQuery = (operator) => {
    const cleanedKeyword = keyword.trim().replace(/\s+/g, ' ');
    if (cleanedKeyword) {
      let newQuery = '';
      if (!query) {
        newQuery = `(${cleanedKeyword})`;
      } else {
        newQuery = `${query} ${operator} (${cleanedKeyword})`;
      }
      updateQuery(newQuery);
      setKeyword('');
    }
  };

  const handleQueryChange = (event) => {
    setQuery(event.target.value);
  };

  const handleDropdownChange = (e) => {
    const operator = e.target.value;
    setDropdownValue(operator);
    addKeywordToQuery(operator);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeywordToQuery(dropdownValue);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Clear saved state when starting a new search
    try {
      localStorage.removeItem(STATE_KEYS.HOMEPAGE_STATE);
      console.log('Homepage state cleared for new search');
    } catch (error) {
      console.error('Error clearing homepage state:', error);
    }
    
    if (startDate == null) {
      showNotification("Start date cannot be empty!", "warning");
      setLoading(false);
      fetchSearchCriteria();
      return;
    }
    else {
      var finalEndDate = endDate;
      if (endDate == null) {
        finalEndDate = new Date();
        setEndDate(finalEndDate);
        console.log("end date ", endDate);
      }
      const queryData = {
        query: query.trim(),
        startDate: startDate ? startDate.toISOString().split('T')[0] : null,
        endDate: finalEndDate ? finalEndDate.toISOString().split('T')[0] : null,
      };

      console.log("queryData ", queryData);
      
      fetchWithAuth('/process', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(queryData),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then(async (data) => {
          console.log('✅ Query submitted successfully:', data);
          showNotification('Query submitted! Processing articles...', 'info');
          
          // ✅ CRITICAL: Immediately refresh to show the new empty query in UI
          await fetchSearchCriteria(true, true);
          
          // Clear the query input after successful submission
          setQuery('');
          
          try {
            // Note: This old flow doesn't create query with /add-search first,
            // so we don't have a query ID to verify articles against.
            // Just wait and refresh after a reasonable delay.
            console.log('⏳ Waiting for articles to be fetched and saved...');
            
            // Wait for fetch to complete (simple delay for this legacy flow)
            await new Promise(resolve => setTimeout(resolve, 5000));
            
            console.log('✅ Refreshing to show articles...');
            showNotification('Articles fetched! Refreshing display...', 'info');
            
            // Refresh data
            try {
              await fetchSearchCriteria(true, true);
              showNotification('Articles loaded successfully!', 'success');
            } catch (refreshError) {
              console.error('Error refreshing data:', refreshError);
              showNotification('Failed to refresh display. Please refresh the page.', 'warning');
            }
          } catch (error) {
            console.error('❌ Error during fetch process:', error);
            showNotification('Error during fetch. Please refresh the page.', 'warning');
          } finally {
            // ALWAYS clear loading state
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error('❌ Error submitting query:', error);
          showNotification('Error submitting query. Please try again.', 'error');
          setLoading(false);
        });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCriteriaClick = async (criteriaObj, index) => {
    // Set the selected query index
    setSelectedQueryIndex(index);
    
    // Use the existing articlesData instead of calling a separate API
    if (articlesData && articlesData?.[index] && articlesData[index].articles && articlesData[index].articles[0]) {
      const articlesData_ = [{
        criteria: [criteriaObj.criteria],
        start_date: [criteriaObj.start_date],
        end_date: [criteriaObj.end_date],
        articles: [articlesData[index].articles[0]]
      }];
      setArticlesData(articlesData_);
      setOriginalArticlesData(articlesData_); // Also update original
      setDataFetched(true);
      
      // Create a simple table component that accepts data as props
      // eslint-disable-next-line no-unused-vars
      const ArticlesTable = ({ data }) => {
        if (!data || !data[0] || !data[0].articles || !data[0].articles[0]) {
          return <Typography variant="body2" style={{ color: 'var(--text-primary)' }}>No articles available.</Typography>;
        }
        
        const articles = data[0].articles[0];
        
        return (
          <Box sx={{ width: '100%', overflow: 'auto' }}>
            <Typography variant="h6" style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>
              Articles for this query ({articles.length} articles)
            </Typography>
            {articles.map((article, index) => (
              <Accordion 
                key={index} 
                className="modern-card" 
                sx={{ 
                  mb: 1,
                  backgroundColor: 'var(--surface-2)',
                  border: '1px solid var(--glass-border)',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon style={{ color: 'var(--neon-cyan)' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      margin: '0.5rem 0'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography 
                      variant="subtitle1" 
                      style={{ 
                        color: 'var(--text-primary)', 
                        fontWeight: 'bold',
                        marginBottom: '0.5rem'
                      }}
                    >
                      {article.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {article.authors} • {article.year} • {article.journal}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" style={{ color: 'var(--text-primary)' }}>
                      <strong>Abstract:</strong> {article.abstract}
                    </Typography>
                    {article.link && (
                      <Typography variant="body2">
                        <a 
                          href={article.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            color: 'var(--neon-cyan)', 
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          🔗 View Article
                        </a>
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );
      };
      

    } else {
      console.log('No articles data available for criteria click, index:', index);
    }
  };

  const handleAccordionChange = async (index, expanded) => {
    setExpandedAccordions(prev => ({ ...prev, [index]: expanded }));
    
    // Set the selected query index when expanding
    if (expanded) {
      setSelectedQueryIndex(index);
    }
    
    // Load articles from articlesData if not already loaded
    if (expanded && !accordionArticles[index]) {
      if (articlesData && articlesData?.[index] && articlesData[index].articles && articlesData[index].articles[0]) {
        console.log('📦 Loading articles from articlesData for index:', index);
        const articles = articlesData[index].articles[0];
        const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
        const formattedArticles = articles.map((article, articleIndex) => ({
          id: toRowId(article),
          ...article,
          // Preserve backend-provided query_id/article_index if they exist (e.g., from category filter)
          // Otherwise use loop indices (for regular queries)
          query_id: article.query_id !== undefined ? article.query_id : index,
          query_index: article.query_index !== undefined ? article.query_index : index,
          article_index: article.article_index !== undefined ? article.article_index : articleIndex,
          read: article.read || false,
          read_date: article.read_date || null,
        }));
        setAccordionArticles(prev => ({ ...prev, [index]: formattedArticles }));
      }
    }
    
    // State will be saved automatically by debounced system
  };

  // Toggle visibility of "no" articles for a specific query
  const toggleNoArticlesVisibility = (queryIndex) => {
    setHiddenNoArticles(prev => ({
      ...prev,
      [queryIndex]: !prev[queryIndex]
    }));
  };

  // Generate unique article ID for annotations
  const generateArticleId = (article, queryIndex) => {
    // Get query title - handle both regular queries and category-filtered views
    let queryTitle = 'Unknown Query';
    if (articlesData && articlesData[queryIndex]) {
      // Try criteria array first (regular queries)
      if (articlesData[queryIndex].criteria && articlesData[queryIndex].criteria[0]) {
        queryTitle = articlesData[queryIndex].criteria[0];
      }
      // Fallback to search_criteria string (category-filtered queries)
      else if (articlesData[queryIndex].search_criteria) {
        queryTitle = articlesData[queryIndex].search_criteria;
      }
    }
    
    // Include more identifying information to make the ID more unique
    const authors = article.authors ? article.authors.replace(/[^a-zA-Z0-9]/g, '_') : 'no_authors';
    const year = article.year || 'no_year';
    const journal = article.journal ? article.journal.replace(/[^a-zA-Z0-9]/g, '_') : 'no_journal';
    const annotationId = `${queryTitle}_${article.title}_${authors}_${year}_${journal}_${article.id || Date.now()}`;
    
    return annotationId;
  };

  const handleRowClick = async (article) => {
    // Check if article is already open
    const existingIndex = openArticles.findIndex(openArticle => 
      article.id === openArticle.id && article.title === openArticle.title
    );
    
    if (existingIndex === -1) {
      // Add new article to open articles with unique ID for annotations
      const newOpenArticle = {
        ...article,
        tabId: `article-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        annotationId: generateArticleId(article, selectedQueryIndex),
        // Ensure the article has the correct query_index and article_index for database updates
        query_index: article.query_index !== undefined ? article.query_index : selectedQueryIndex,
        article_index: article.article_index !== undefined ? article.article_index : article.id
      };
      
      setOpenArticles(prev => [...prev, newOpenArticle]);
      setActiveArticleTab(newOpenArticle.tabId);
      
      // Force user to Article tab during tutorial and auto-complete step 9
      if (isTutorialActive) {
        setActiveTab('articles');
        if (getCurrentStep && getCurrentStep()?.id === 'open_article') {
          completeStep('open_article');
        }
      }
      
      // Mark article as read when opened (if it's unread)
      if (!article.read) {
        await updateReadStatus(newOpenArticle, true);
      }
    } else {
      // Switch to existing article tab
      setActiveArticleTab(openArticles[existingIndex].tabId);
      
      // Force user to Article tab during tutorial
      if (isTutorialActive) {
        setActiveTab('articles');
      }
    }
    
    // State will be saved automatically by debounced system
  };

  // Function to update read status in both table and detail view
  const updateReadStatus = async (article, newReadStatus) => {
    try {
      
      // Set flag to prevent automatic data updates
      setManualReadStatusChange(true);
      
      const response = await fetchWithAuth('/api/update-read-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // 🛡️ SAFETY: Send unique_query_id for new queries, query_id for legacy
          unique_query_id: article.unique_query_id,  // Unique ID (safe)
          query_id: typeof article.query_index === 'number' ? article.query_index : selectedQueryIndex,  // Index (legacy fallback)
          article_id: typeof article.article_index === 'number' || typeof article.article_index === 'string' ? article.article_index : 0,
          read: newReadStatus,
          read_date: new Date().toISOString(),
        }),
      });
      if (response.ok) {
        
        // Update the article in openArticles
        setOpenArticles(prev => prev.map(openArticle => 
          openArticle.tabId === article.tabId 
            ? { ...openArticle, read: newReadStatus, read_date: newReadStatus ? new Date().toISOString() : null }
            : openArticle
        ));
        
        // Update the data state to reflect the change immediately
        if (data) {
          setData(prevData => 
            prevData.map(item => 
              item.id === article.id || 
              (item.title === article.title && item.authors === article.authors)
                ? { ...item, read: newReadStatus, read_date: newReadStatus ? new Date().toISOString() : null }
                : item
            )
          );
        }
        
        // Also update articlesData if it exists
        if (articlesData) {
          const updater = prevData => 
            prevData.map(queryData => ({
              ...queryData,
              articles: queryData.articles.map(articleList => 
                articleList.map(art => 
                  art.id === article.id || 
                  (art.title === article.title && art.authors === article.authors)
                    ? { ...art, read: newReadStatus, read_date: newReadStatus ? new Date().toISOString() : null }
                    : art
                )
              )
            }));
          
          setArticlesData(updater);
          setOriginalArticlesData(updater); // Also update original!
        }
        
        // Update accordionArticles to sync with table - UPDATE ALL QUERIES!
        if (accordionArticles) {
          setAccordionArticles(prev => {
            const updated = { ...prev };
            // Update the article in ALL accordions (not just selected query)
            Object.keys(updated).forEach(queryIdx => {
              if (updated[queryIdx]) {
                updated[queryIdx] = updated[queryIdx].map(art => 
              art.id === article.id || 
              (art.title === article.title && art.authors === article.authors)
                ? { ...art, read: newReadStatus, read_date: newReadStatus ? new Date().toISOString() : null }
                : art
                );
              }
            });
            return updated;
          });
        }
        
        
        // Reset flag after a longer delay to ensure all updates complete
        setTimeout(() => {
          setManualReadStatusChange(false);
        }, 1000);
      }
    } catch (error) {
      console.error('Error updating read status:', error);
      // Reset flag even on error
      setTimeout(() => {
        setManualReadStatusChange(false);
      }, 1000);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetchWithAuth('/api/collaboration/users');
      const data = await response.json();
      
      if (data.error) {
        console.error('Error loading users:', data.error);
      } else {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleShareArticle = (article) => {
    console.log('📤 Sharing article from tab:', article);
    
    // Set the article to share and open the share dialog
    setSelectedArticle(article);
    setShareDialogOpen(true);
    loadUsers(); // Load users for selection
  };

  const handleShareToUser = async () => {
    if (!selectedUser || !selectedArticle) return;
    
    try {
      setSending(true);
      
      const response = await fetchWithAuth('/api/collaboration/share-items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_id: selectedUser.id,
          items: [{
            type: 'article',
            id: selectedArticle.id,
            title: selectedArticle.title || 'No title',
            authors: selectedArticle.authors || 'Unknown Authors',
            journal: selectedArticle.journal || 'Unknown Journal',
            year: selectedArticle.year || 'Unknown Year',
            abstract: selectedArticle.abstract || 'No abstract available',
            description: selectedArticle.description || '',
            url: selectedArticle.link || selectedArticle.url || ''
          }],
          share_type: 'article',
          message: shareMessage.trim() || `Shared article with you`
        })
      });
      
      const data = await response.json();
      
      if (data.error) {
        showNotification('Error sharing article: ' + data.error, 'error');
      } else {
        showNotification('Article shared successfully!', 'success');
        // Track the last shared user and save to localStorage
        setLastSharedUser(selectedUser);
        try {
          localStorage.setItem('lastSharedUser', JSON.stringify(selectedUser));
        } catch (error) {
          console.error('Error saving last shared user:', error);
        }
        setShareDialogOpen(false);
        setSelectedUser(null);
        setShareMessage('');
        setUserSearchQuery('');
        setSelectedArticle(null);
      }
    } catch (err) {
      console.error('Error sharing article:', err);
      showNotification('Error sharing article: ' + err.message, 'error');
    } finally {
      setSending(false);
    }
  };

  const handleCloseArticleTab = (tabId) => {
    // Don't allow closing the queries tab
    if (tabId === QUERIES_TAB_ID) return;
    
    setOpenArticles(prev => prev.filter(article => article.tabId !== tabId));
    
    // If we're closing the active tab, switch to queries tab or another tab
    if (activeArticleTab === tabId) {
      const remainingArticles = openArticles.filter(article => article.tabId !== tabId);
      if (remainingArticles.length > 0) {
        setActiveArticleTab(remainingArticles[0].tabId);
      } else {
        setActiveArticleTab(QUERIES_TAB_ID);
      }
    }
  };

  const handleCloseAllArticleTabs = () => {
    setOpenArticles([]);
    setActiveArticleTab(QUERIES_TAB_ID);
  };

  return (
    <>
      {/* Full Screen Container - Excluding Navigation */}
      <div style={{ 
        width: '100%',
        height: 'calc(100vh - 60px)', // Full height minus navigation bar
        background: 'var(--surface)',
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
        marginTop: '0rem'
      }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
            <div className="loading-spinner-modern"></div>
          </Box>
        ) : (
          <>
            {/* Main Content Area */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              overflow: 'hidden', 
              padding: '1rem 1rem 2rem 1rem'  // Fixed padding - left panel width is now constant
            }}>
          <Grid container spacing={1.5} style={{ height: '100%', overflow: 'hidden', margin: 0, alignItems: 'flex-start', maxHeight: '100%', position: 'relative' }}>
            {/* Left Panel - Search Interface - FIXED WIDTH */}
                <Grid item xs={12} md={3} style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Search Form */}
              <div className="modern-card" style={{ height: 'calc(100% - 200px)', display: 'flex', flexDirection: 'column', overflow: 'auto', marginBottom: '1rem' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center">
                  <ScienceIcon style={{ color: 'var(--neon-cyan)', marginRight: '0.5rem' }} />
                  <Typography variant="h6" className="text-neon">
                    Research Portal
                  </Typography>
                  </Box>
                  <Button
                    data-tutorial="categories-button"
                    size="small"
                    onClick={() => {
                      setShowCategories(!showCategories);
                      if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'open_categories_panel') {
                        completeStep('open_categories_panel');
                      }
                    }}
                    startIcon={showCategories ? <ArrowBackIcon /> : null}
                    endIcon={!showCategories ? <ArrowForwardIcon /> : null}
                    className="btn-modern"
                    sx={getButtonStyles(showCategories, isDarkMode, 'generic')}
                  >
                    Categories
                  </Button>
                </Box>
                
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', paddingTop: '0.75rem' }}>
                  <Grid container spacing={2} style={{ flex: 1 }}>
                    {/* Keyword field */}
                    <Grid item xs={12}>
                      <Box data-tutorial="keyword-input" sx={{ position: 'relative', zIndex: 1 }}>
                        <TextField
                          fullWidth
                          label="Keyword"
                          variant="outlined"
                          value={keyword}
                          onChange={(e) => setKeyword(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Enter research keyword..."
                          className="input-modern"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'var(--text-primary)',
                              '& fieldset': {
                                borderColor: 'var(--glass-border)',
                              },
                              '&:hover fieldset': {
                                borderColor: 'var(--neon-cyan)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'var(--neon-cyan)',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'var(--text-primary)',
                              zIndex: 2,
                              '&.Mui-focused': {
                                color: 'var(--neon-cyan)',
                              },
                              '&.MuiInputLabel-shrink': {
                                backgroundColor: 'var(--surface-2)',
                                paddingLeft: '4px',
                                paddingRight: '4px',
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    
                    {/* Operator field right below keyword */}
                    <Grid item xs={12}>
                      <FormControl fullWidth className="select-modern">
                        <InputLabel 
                          id="operator-select-label" 
                          sx={{ 
                            color: 'var(--text-muted)',
                            '&.Mui-focused': {
                              color: '#6c3082'
                            }
                          }}
                        >
                          Operator
                        </InputLabel>
                        <Select
                          labelId="operator-select-label"
                          id="operator-select"
                          value={dropdownValue}
                          onChange={handleDropdownChange}
                          label="Operator"
                          data-tutorial="operator-select"
                          sx={{
                            color: 'var(--text-primary)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--glass-border)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--neon-cyan)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--neon-cyan)',
                            },
                            '& .MuiSelect-icon': {
                              color: 'var(--text-primary)',
                            },
                            '& .MuiSelect-select': {
                              backgroundColor: 'var(--surface-2)',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: 'var(--surface-2)',
                                border: '1px solid var(--glass-border)',
                                borderRadius: 'var(--radius-md)',
                                '& .MuiMenuItem-root': {
                                  color: 'var(--text-primary)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'var(--accent-gradient)',
                                    color: '#ffffff',
                                    '&:hover': {
                                      backgroundColor: 'var(--accent-gradient)',
                                    },
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="and">AND</MenuItem>
                          <MenuItem value="or">OR</MenuItem>
                          <MenuItem value="not">NOT</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* Query box with reduced size */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Query"
                        variant="outlined"
                        multiline
                        rows={2}
                        value={query}
                        onChange={handleQueryChange}
                        placeholder="Your research query will appear here..."
                        className="input-modern"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'var(--text-primary)',
                            '& fieldset': {
                              borderColor: 'var(--glass-border)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'var(--neon-cyan)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'var(--neon-cyan)',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'var(--text-primary)',
                            zIndex: 2,
                            '&.Mui-focused': {
                              color: 'var(--neon-cyan)',
                            },
                            '&.MuiInputLabel-shrink': {
                              backgroundColor: 'var(--surface-2)',
                              paddingLeft: '4px',
                              paddingRight: '4px',
                            },
                          },
                        }}
                      />
                    </Grid>
                    
                    {/* Date Range */}
                    <Grid item xs={6}>
                      <Box data-tutorial="start-date">
                        <Typography variant="body2" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                          Start Date
                        </Typography>
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => setStartDate(date ? handleDateConversion(date) : null)}
                          placeholderText="Select start date"
                          className="form-control"
                          dateFormat="yyyy-MM-dd"
                          isClearable
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          style={{
                            width: '100%',
                            background: 'var(--surface-2)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text-primary)',
                            padding: '0.75rem'
                          }}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Box data-tutorial="end-date">
                        <Typography variant="body2" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                          End Date
                        </Typography>
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => setEndDate(date ? handleDateConversion(date) : null)}
                          placeholderText="Select end date"
                          className="form-control"
                          dateFormat="yyyy-MM-dd"
                          isClearable
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          style={{
                            width: '100%',
                            background: 'var(--surface-2)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text-primary)',
                            padding: '0.75rem'
                          }}
                        />
                      </Box>
                    </Grid>
                    
                    {/* Domain Selection */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                      <FormControl fullWidth className="select-modern">
                        <InputLabel 
                          id="domain-select-label" 
                          sx={{ 
                            color: 'var(--text-muted)',
                            '&.Mui-focused': {
                              color: '#6c3082'
                            }
                          }}
                        >
                          Research Domain (for AI Filtering)
                        </InputLabel>
                        <Select
                          labelId="domain-select-label"
                          id="domain-select"
                          value={selectedDomain}
                          onChange={(e) => setSelectedDomain(e.target.value)}
                          label="Research Domain (for AI Filtering)"
                          data-tutorial="domain-select"
                          sx={{
                            color: 'var(--text-primary)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--glass-border)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--neon-cyan)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--neon-cyan)',
                            },
                            '& .MuiSelect-icon': {
                              color: 'var(--text-primary)',
                            },
                            '& .MuiSelect-select': {
                              backgroundColor: 'var(--surface-2)',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: 'var(--surface-2)',
                                border: '1px solid var(--glass-border)',
                                borderRadius: 'var(--radius-md)',
                                '& .MuiMenuItem-root': {
                                  color: 'var(--text-primary)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'var(--accent-gradient)',
                                    color: '#ffffff',
                                    '&:hover': {
                                      backgroundColor: 'var(--accent-gradient)',
                                    },
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="">No AI Filtering</MenuItem>
                          
                          {/* Predefined Domains */}
                          {availableDomains.filter(d => !d.is_custom).length > 0 && (
                            <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.75rem' }}>
                              — PREDEFINED MODELS —
                            </MenuItem>
                          )}
                          {availableDomains.filter(d => !d.is_custom).map((domain) => (
                            <MenuItem key={domain.key} value={domain.key}>
                              {domain.key} ({domain.name.charAt(0).toUpperCase() + domain.name.slice(1)})
                            </MenuItem>
                          ))}
                          
                          {/* Custom Domains */}
                          {availableDomains.filter(d => d.is_custom).length > 0 && (
                            <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.75rem', mt: 1 }}>
                              — MY CUSTOM MODELS —
                            </MenuItem>
                          )}
                          {availableDomains.filter(d => d.is_custom).map((domain) => (
                            <MenuItem key={domain.key} value={domain.key}>
                              ⭐ {domain.name} ({domain.annotation_count} annotations)
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                        
                        {/* Delete Domain Button - Only show for custom domains */}
                        {selectedDomain && (() => {
                          const domain = availableDomains.find(d => d.key === selectedDomain);
                          return domain && domain.is_custom ? (
                            <Button
                              variant="contained"
                              onClick={() => handleDeleteDomainClick(selectedDomain)}
                              className="btn-delete btn-icon-small"
                              sx={{ 
                                minWidth: 'unset !important',
                                width: '28px !important',
                                maxWidth: '28px !important',
                                height: '56px !important',
                                padding: '0 !important',
                                flexShrink: 0,
                                color: '#ffffff !important',
                                fontSize: '0 !important',
                                '& .MuiSvgIcon-root': {
                                  color: '#ffffff !important',
                                  margin: '0 !important'
                                },
                                '&.MuiButton-root': {
                                  minWidth: 'unset !important',
                                  width: '28px !important',
                                  padding: '0 !important'
                                }
                              }}
                              title="Delete Custom Domain"
                            >
                              <DeleteIcon />
                            </Button>
                          ) : null;
                        })()}
                        
                        {/* Create Domain Button */}
                        <Button
                          variant="outlined"
                          onClick={() => setCreateDomainOpen(true)}
                          className="btn-icon-small"
                          sx={{ 
                            minWidth: 'unset !important',
                            width: '28px !important',
                            maxWidth: '28px !important',
                            height: '56px !important',
                            padding: '0 !important',
                            flexShrink: 0,
                            borderColor: 'transparent !important',
                            color: 'var(--text-primary) !important',
                            fontSize: '0 !important',
                            '& .MuiSvgIcon-root': {
                              color: 'var(--text-primary) !important',
                              margin: '0 !important'
                            },
                            '&:hover': {
                              backgroundColor: BUTTON_COLORS.hoverFill,
                              borderColor: `${BUTTON_COLORS.borderHover} !important`
                            },
                            '&.MuiButton-root': {
                              minWidth: 'unset !important',
                              width: '28px !important',
                              padding: '0 !important'
                            }
                          }}
                          title="Create New Domain"
                        >
                          <AddIcon />
                        </Button>
                      </Box>
                      
                      {/* Progress Indicator for Selected Domain */}
                      {selectedDomain && (() => {
                        const domain = availableDomains.find(d => d.key === selectedDomain);
                        if (domain && domain.is_custom) {
                          const yesCount = domain.annotation_count || 0;
                          const remaining = Math.max(0, 50 - yesCount);
                          const progress = Math.min(100, (yesCount / 50) * 100);
                          
                          return (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="var(--text-muted)" sx={{ display: 'block', mb: 0.5 }}>
                                Training Progress: {yesCount}/50 annotations
                                {remaining > 0 && ` (${remaining} more needed)`}
                              </Typography>
                              <Box sx={{ 
                                width: '100%', 
                                height: '4px', 
                                bgcolor: 'var(--glass-border)', 
                                borderRadius: '2px',
                                overflow: 'hidden'
                              }}>
                                <Box sx={{ 
                                  width: `${progress}%`, 
                                  height: '100%', 
                                  bgcolor: progress >= 100 ? 'var(--success)' : 'var(--neon-cyan)',
                                  transition: 'width 0.3s ease'
                                }} />
                              </Box>
                            </Box>
                          );
                        }
                        return null;
                      })()}
                    </Grid>
                  </Grid>
                  
                  {/* Buttons at the bottom */}
                  <Box display="flex" gap={1} mt={2}>
                    <Button
                      type="button"
                      className="btn-modern"
                      startIcon={<SaveIcon />}
                      onClick={handleAddCriteria}
                      fullWidth
                      data-tutorial="fetch-button"
                      sx={getButtonStyles(false, isDarkMode, 'generic')}
                    >
                      Save Query
                    </Button>
                  </Box>
                </Box>
              </div>

              {/* Tab Navigation */}
              <div className="modern-card" style={{ flex: '0 0 auto', marginBottom: '1rem' }}>
                <Box display="flex" gap={1}>
                  <Button
                    onClick={() => setActiveTab('queries')}
                    startIcon={<TimelineIcon />}
                    fullWidth
                    className="btn-modern"
                    sx={getButtonStyles(activeTab === 'queries', isDarkMode, 'generic')}
                  >
                    Queries
                  </Button>
                  <Button
                    onClick={() => setActiveTab('insights')}
                    startIcon={<AutoAwesomeIcon />}
                    fullWidth
                    className="btn-modern"
                    sx={getButtonStyles(activeTab === 'insights', isDarkMode, 'generic')}
                  >
                    Insights
                  </Button>
                </Box>
              </div>

              {/* Statistics Section */}
              <div className="modern-card" style={{ flex: '0 0 auto', marginBottom: '1rem' }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  <TimelineIcon style={{ color: 'var(--neon-cyan)', marginRight: '0.5rem', fontSize: '1.2rem' }} />
                  <Typography variant="subtitle1" className="text-neon">
                    Statistics
                  </Typography>
                </Box>
                
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        p: 1, 
                        backgroundColor: 'rgba(70, 130, 48, 0.1)', 
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid #468230'
                      }}
                    >
                      <Typography variant="h6" style={{ color: '#468230', fontWeight: 'bold', margin: 0 }}>
                        {articlesData ? articlesData.reduce((total, queryData) => {
                          if (queryData.articles && queryData.articles[0]) {
                            return total + queryData.articles[0].length;
                          }
                          return total;
                        }, 0) : 0}
                      </Typography>
                      <Typography variant="caption" style={{ color: 'var(--text-primary)' }}>
                        Total Articles
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        p: 1, 
                        backgroundColor: 'rgba(70, 130, 48, 0.1)', 
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid #468230'
                      }}
                    >
                      <Typography variant="h6" style={{ color: '#468230', fontWeight: 'bold', margin: 0 }}>
                        {searchCriteria ? searchCriteria.length : 0}
                      </Typography>
                      <Typography variant="caption" style={{ color: 'var(--text-primary)' }}>
                        Queries
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* Middle Panel - Categories (Collapsible) */}
            {showCategories && (
              <Grid 
                item 
                xs={12} 
                md={3} 
                style={{ 
                  height: 'calc(100% - 1rem)', 
                  overflow: 'hidden', 
                  maxHeight: 'calc(100% - 1rem)',
                  animation: 'expandIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 1
                }}
              >
                <div className="modern-card" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0, animation: 'fadeIn 0.3s ease-in' }}>
                  <CategorySidebar
                    categories={categories}
                    selectedCategory={selectedCategoryFilter}
                    onSelectCategory={handleSelectCategory}
                    totalArticles={calculateTotalArticles()}
                    uncategorizedCount={calculateUncategorizedCount()}
                    onCreateCategory={handleCreateCategoryFromSidebar}
                    onEditCategory={handleEditCategory}
                    onDeleteCategory={handleDeleteCategory}
                    onClose={() => setShowCategories(false)}
                  />
                </div>
              </Grid>
            )}

            {/* Right Panel - Results - Shrinks INSTANTLY when categories open */}
            <Grid 
              item 
              xs={12} 
              md={showCategories ? 6 : 9} 
              style={{ 
                height: 'calc(100% - 1rem)', 
                overflow: 'hidden', 
                maxHeight: 'calc(100% - 1rem)',
                transition: 'opacity 0.15s ease-in-out',
                opacity: 1
              }}
            >
              <div 
                className="modern-card" 
                style={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  overflow: 'hidden',
                  transition: 'opacity 0.2s ease-in-out',
                  opacity: 1
                }}
              >
                {/* Tab Headers - Always visible */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: 0, 
                  borderBottom: '1px solid var(--glass-border)',
                  flexShrink: 0 
                }}>
                  <Box sx={{ display: 'flex', overflow: 'auto', flex: 1 }}>
                    {/* Permanent Queries Tab */}
                    <Box
                      className={activeArticleTab === QUERIES_TAB_ID ? 'btn-modern-primary' : 'btn-modern-secondary'}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                        minWidth: 'fit-content',
                        borderRadius: '4px',
                        margin: '0.25rem',
                        transition: 'all 0.2s ease',
                        fontWeight: activeArticleTab === QUERIES_TAB_ID ? 600 : 500,
                        fontSize: '0.95rem',
                        border: activeArticleTab === QUERIES_TAB_ID 
                          ? '1px solid rgba(108, 48, 130, 0.3) !important' 
                          : isDarkMode 
                            ? '1px solid rgba(255, 255, 255, 0.3) !important' 
                            : '1px solid rgba(0, 0, 0, 0.2) !important',
                        '&.MuiBox-root': {
                          border: activeArticleTab === QUERIES_TAB_ID 
                            ? '1px solid rgba(108, 48, 130, 0.3) !important' 
                            : isDarkMode 
                              ? '1px solid rgba(255, 255, 255, 0.3) !important' 
                              : '1px solid rgba(0, 0, 0, 0.2) !important'
                        },
                        '&:hover': {
                          background: activeArticleTab === QUERIES_TAB_ID ? 'var(--accent-gradient)' : 'rgba(108, 48, 130, 0.08)',
                          borderColor: '#6c3082 !important',
                          opacity: activeArticleTab === QUERIES_TAB_ID ? 0.9 : 1
                        }
                      }}
                      onClick={() => {
                        setActiveArticleTab(QUERIES_TAB_ID);
                        // State will be saved automatically by debounced system
                      }}
                    >
                                              <Typography 
                          variant="body2" 
                          style={{ 
                            color: activeArticleTab === QUERIES_TAB_ID ? '#ffffff' : 'var(--text-primary)',
                            fontWeight: activeArticleTab === QUERIES_TAB_ID ? 'bold' : 'normal',
                          }}
                        >
                          {activeTab === 'insights' ? '✨ Insights' : '📋 Queries'}
                        </Typography>
                    </Box>
                    
                    {/* Article Tabs - Hidden when in Insights view */}
                    {activeTab !== 'insights' && openArticles.map((article) => (
                      <Box
                        key={article.tabId}
                        className={activeArticleTab === article.tabId ? 'btn-modern-primary' : 'btn-modern-secondary'}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1,
                          cursor: 'pointer',
                          minWidth: 'fit-content',
                          borderRadius: '4px',
                          margin: '0.25rem',
                          transition: 'all 0.2s ease',
                          fontWeight: activeArticleTab === article.tabId ? 600 : 500,
                          fontSize: '0.95rem',
                          border: activeArticleTab === article.tabId 
                            ? '1px solid rgba(108, 48, 130, 0.3) !important' 
                            : isDarkMode 
                              ? '1px solid rgba(255, 255, 255, 0.3) !important' 
                              : '1px solid rgba(0, 0, 0, 0.2) !important',
                          '&.MuiBox-root': {
                            border: activeArticleTab === article.tabId 
                              ? '1px solid rgba(108, 48, 130, 0.3) !important' 
                              : isDarkMode 
                                ? '1px solid rgba(255, 255, 255, 0.3) !important' 
                                : '1px solid rgba(0, 0, 0, 0.2) !important'
                          },
                          '&:hover': {
                            background: activeArticleTab === article.tabId ? 'var(--accent-gradient)' : 'rgba(108, 48, 130, 0.08)',
                            borderColor: '#6c3082 !important',
                            opacity: activeArticleTab === article.tabId ? 0.9 : 1
                          }
                        }}
                        onClick={() => {
                          setActiveArticleTab(article.tabId);
                          // State will be saved automatically by debounced system
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          style={{ 
                            color: activeArticleTab === article.tabId ? '#ffffff' : 'var(--text-primary)',
                            fontWeight: activeArticleTab === article.tabId ? 'bold' : 'normal',
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {article.title.length > 25 ? `${article.title.substring(0, 25)}...` : article.title}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareArticle(article);
                          }}
                          sx={{ 
                            color: activeArticleTab === article.tabId ? '#ffffff !important' : 'var(--neon-cyan)',
                            '&:hover': { color: activeArticleTab === article.tabId ? '#ffffff !important' : 'var(--success)' },
                            p: 0.5,
                            '& .MuiSvgIcon-root': {
                              color: 'inherit !important'
                            }
                          }}
                          title="Share Article"
                        >
                          <ShareIcon fontSize="small" sx={{ color: 'inherit !important' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseArticleTab(article.tabId);
                          }}
                          sx={{ 
                            color: activeArticleTab === article.tabId ? '#ffffff !important' : 'var(--text-primary)',
                            '&:hover': { color: activeArticleTab === article.tabId ? '#ffffff !important' : 'var(--neon-cyan)' },
                            p: 0.5,
                            '& .MuiSvgIcon-root': {
                              color: 'inherit !important'
                            }
                          }}
                          title="Close Article Tab"
                        >
                          <CloseIcon fontSize="small" sx={{ color: 'inherit !important' }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
                
                {/* Tab Content */}
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, position: 'relative' }}>
                  {/* Queries Tab Content */}
                  <Box
                    sx={{
                      display: activeArticleTab === QUERIES_TAB_ID ? 'block' : 'none',
                      height: '100%',
                      overflow: 'auto'
                    }}
                  >
                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                      {activeTab === 'insights' && (
                        <ResearchInsights 
                          searchCriteria={searchCriteria}
                          onArticleClick={handleRowClick}
                        />
                      )}

                      {activeTab === 'queries' && (
                        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, '& > *': { marginBottom: '0px !important' }, paddingTop: '0.5rem' }}>
                          {searchCriteria.map((criteriaObj, index) => (
                            <Accordion 
                              key={index}
                              sx={{ 
                                marginBottom: '1rem !important',
                                backgroundColor: 'var(--surface-2)',
                                border: '1px solid var(--glass-border)',
                                borderRadius: 'var(--radius-lg)',
                                boxShadow: 'var(--shadow-md)',
                                '&:before': { display: 'none' },
                                '&.MuiAccordion-root': {
                                  marginBottom: '1rem !important'
                                },
                                '& + .MuiAccordion-root': {
                                  marginTop: '0px !important'
                                }
                              }}
                              expanded={expandedAccordions[index]}
                              onChange={(event, isExpanded) => handleAccordionChange(index, isExpanded)}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon style={{ color: 'var(--neon-cyan)' }} />}
                                sx={{
                                  position: expandedAccordions[index] ? 'sticky' : 'static',
                                  top: 0,
                                  zIndex: 10,
                                  backgroundColor: 'var(--surface-2)',
                                  borderBottom: expandedAccordions[index] ? '1px solid var(--border)' : 'none',
                                  borderRadius: expandedAccordions[index] ? 'var(--radius-md) var(--radius-md) 0 0' : 'var(--radius-md)',
                                  '& .MuiAccordionSummary-content': {
                                    margin: '0.5rem 0'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <Typography 
                                      variant="h6" 
                                      style={{ 
                                        color: 'var(--text-primary)', 
                                        fontWeight: 'bold',
                                        marginBottom: '0.25rem',
                                        fontSize: '1.1rem'
                                      }}
                                    >
                                      {criteriaObj.criteria}
                                      {(originalAiFilteringSummaries[index] || articlesData?.[index]?.articles?.[0]) && (
                                        <Typography component="span" style={{ 
                                          color: 'var(--text-muted)', 
                                          fontWeight: 'normal',
                                          marginLeft: '0.5rem'
                                        }}>
                                          - {originalAiFilteringSummaries[index]?.total || articlesData?.[index]?.articles?.[0]?.length || 0} articles
                                        </Typography>
                                      )}
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      style={{ color: 'var(--text-muted)' }}
                                    >
                                      {criteriaObj.start_date} - {criteriaObj.end_date || 'Present'}
                                    </Typography>
                                    
                                    {/* AI Filtering Results in Accordion Header */}
                                    {aiFilteringSummaries[index] && (
                                      <Box sx={{ mt: 1 }}>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                          <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.5,
                                            padding: '2px 6px',
                                            backgroundColor: 'rgba(70, 130, 48, 0.25)',
                                            borderRadius: 'var(--radius-sm)',
                                            border: '1px solid var(--success)'
                                          }}>
                                            <Typography variant="caption" sx={{ color: 'var(--success)', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                              ✓ Selected: {aiFilteringSummaries[index].selected}
                                            </Typography>
                                          </Box>
                                          
                                          <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.5,
                                            padding: '2px 6px',
                                            backgroundColor: 'rgba(255, 193, 7, 0.25)',
                                            borderRadius: 'var(--radius-sm)',
                                            border: '1px solid var(--warning)'
                                          }}>
                                            <Typography variant="caption" sx={{ color: 'var(--warning)', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                              ? Maybe: {aiFilteringSummaries[index].maybe}
                                            </Typography>
                                          </Box>
                                          
                                          <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.5,
                                            padding: '2px 6px',
                                            backgroundColor: 'rgba(244, 67, 54, 0.25)',
                                            borderRadius: 'var(--radius-sm)',
                                            border: '1px solid var(--error)'
                                          }}>
                                            <Typography variant="caption" sx={{ color: 'var(--error)', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                              ✗ Not Selected: {originalAiFilteringSummaries[index]?.notSelected || aiFilteringSummaries[index].notSelected}
                                              {hiddenNoArticles[index] && (
                                                <Typography component="span" sx={{ 
                                                  color: 'var(--text-muted)', 
                                                  fontSize: '0.65rem',
                                                  fontWeight: 'normal',
                                                  ml: 0.5,
                                                  fontStyle: 'italic'
                                                }}>
                                                  (hidden)
                                                </Typography>
                                              )}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {/* Hide/Show "No" Articles Toggle */}
                                    {expandedAccordions[index] && originalAiFilteringSummaries[index] && originalAiFilteringSummaries[index].notSelected > 0 && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleNoArticlesVisibility(index);
                                        }}
                                        className="btn-modern-primary"
                                        sx={{
                                          fontSize: '0.75rem',
                                          padding: '6px 12px',
                                          minWidth: 'auto',
                                          height: '28px',
                                          background: 'var(--accent-gradient)',
                                          border: 'none',
                                          borderRadius: 'var(--radius-md)',
                                          color: '#ffffff',
                                          fontWeight: '600',
                                          textTransform: 'none',
                                          boxShadow: '0 2px 8px rgba(108, 48, 130, 0.3)',
                                          mr: 1,
                                          '&:hover': {
                                            background: 'linear-gradient(135deg, #5a2a6f 0%, #7d3a92 100%)',
                                            boxShadow: '0 4px 12px rgba(108, 48, 130, 0.4)',
                                            transform: 'translateY(-1px)'
                                          },
                                          '&:active': {
                                            transform: 'translateY(0px)',
                                            boxShadow: '0 2px 6px rgba(108, 48, 130, 0.3)'
                                          },
                                          transition: 'all 0.2s ease-in-out'
                                        }}
                                      >
                                        {hiddenNoArticles[index] ? 'Show All' : 'Hide "No" Articles'}
                                      </Button>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleModifyCriteria(criteriaObj, index);
                                      }}
                                      className="btn-modern-primary btn-icon-small"
                                      sx={{
                                        color: '#ffffff !important',
                                        '& .MuiSvgIcon-root': {
                                          color: '#ffffff !important'
                                        }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveCriteria(index);
                                      }}
                                      className="btn-delete btn-icon-small"
                                      sx={{
                                        color: '#ffffff !important',
                                        boxShadow: 'none !important',
                                        '&:hover': {
                                          boxShadow: 'none !important',
                                          transform: 'none !important'
                                        },
                                        '& .MuiSvgIcon-root': {
                                          color: '#ffffff !important'
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                                <Box sx={{ width: '100%' }}>
                                  {/* This will be populated when the accordion is expanded */}
                                  <Box id={`articles-container-${index}`} sx={{ minHeight: '100px' }}>
                                    {accordionArticles[index] && expandedAccordions[index] ? (
                                      <Box 
                                        data-tutorial="article-table"
                                        sx={{ 
                                          width: '100%', 
                                          backgroundColor: 'var(--surface)',
                                          borderRadius: 'var(--radius-md)',
                                          overflow: 'auto'
                                        }}>
                                        {accordionArticles[index].length > 0 ? (
                                          <DynamicTable 
                                            key={`table-${index}-${accordionArticles[index]?.length || 0}`}
                                            data={accordionArticles[index]} 
                                            domain={searchCriteria[index]?.domain?.[0] || ''}
                                            hiddenNoArticles={hiddenNoArticles[index] || false}
                                            onRowClick={handleRowClick}
                                            onReadStatusChange={async (articleId, newReadStatus) => {
                                              // Find the article in the accordion data
                                              const list = accordionArticles[index] || [];
                                              const article = list.find(item => item.id === articleId);
                                              if (article) {
                                                // Ensure we send correct indices expected by backend
                                                const computedArticleIndex =
                                                  typeof article.article_index === 'number' || typeof article.article_index === 'string'
                                                    ? article.article_index
                                                    : list.findIndex(a => a.id === articleId);
                                                const enriched = {
                                                  ...article,
                                                  query_index: index,
                                                  article_index: computedArticleIndex
                                                };
                                                await updateReadStatus(enriched, newReadStatus);
                                              } else {
                                                console.error('❌ HomePage: Article not found in accordion for ID:', articleId);
                                              }
                                            }}
                                            onArticleUpdate={(updatedArticle) => {
                                              // IMMEDIATELY update parent state so it persists across remounts
                                              console.log('🔄 onArticleUpdate called for query', index);
                                              console.log('  Article ID:', updatedArticle.id);
                                              console.log('  New prediction:', updatedArticle.ai_prediction);
                                              console.log('  Classification modified:', updatedArticle.classification_modified);
                                              
                                              setAccordionArticles(prev => {
                                                console.log('  📦 Current accordionArticles[' + index + '] count:', prev[index]?.length || 0);
                                                const updated = {
                                                  ...prev,
                                                  [index]: prev[index]?.map(article => {
                                                    if (article.id === updatedArticle.id) {
                                                      console.log('    ✅ Found and updating article:', article.id);
                                                      return updatedArticle;
                                                    }
                                                    return article;
                                                  }) || []
                                                };
                                                console.log('  📦 Updated accordionArticles[' + index + '] count:', updated[index]?.length || 0);
                                                return updated;
                                              });
                                            }}
                                            currentPage={tablePagination[index] || 0}
                                            onPageChange={(newPage) => {
                                              console.log(`📄 Page changed for accordion ${index}: ${newPage}`);
                                              setTablePagination(prev => {
                                                const newPagination = {
                                                  ...prev,
                                                  [index]: newPage
                                                };
                                                console.log('💾 Updated tablePagination:', newPagination);
                                                return newPagination;
                                              });
                                              
                                              // State will be saved automatically by debounced system
                                            }}
                                            onAIFilteringSummary={(summary) => {
                                              setAiFilteringSummaries(prev => ({
                                                ...prev,
                                                [index]: summary
                                              }));
                                              // Also save original summary if not already saved
                                              setOriginalAiFilteringSummaries(prev => ({
                                                ...prev,
                                                [index]: prev[index] || summary
                                              }));
                                            }}
                                            categories={categories}
                                            onCategoryChange={handleCategoryChange}
                                          />
                                        ) : (
                                          <Box sx={{ 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            padding: '2rem',
                                            textAlign: 'center'
                                          }}>
                                            <Typography variant="h6" style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                                              No articles match the selected category filter
                                            </Typography>
                                            <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
                                              Try selecting a different category or clear the filter to see all articles
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    ) : !accordionArticles[index] ? (
                                      <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
                                        Loading articles...
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Article Tabs Content */}
                  {openArticles.map((article) => (
                    <Box
                      key={article.tabId}
                      data-tutorial="article-detail-panel"
                      sx={{
                        display: activeArticleTab === article.tabId ? 'block' : 'none',
                        height: '100%',
                        overflow: 'auto'
                      }}
                    >
                      <ArticleAnnotation 
                        article={{
                          ...article,
                          id: article.annotationId || article.id,
                          // Ensure abstract is available (check both abstract and truncated_abstract)
                          abstract: article.abstract || article.truncated_abstract || article.description || ''
                        }}
                        onAnnotationsChange={() => {
                          // Optional: trigger any updates when annotations change
                          console.log('Annotations updated for article:', article.title);
                        }}
                        isActive={activeArticleTab === article.tabId}
                      >
                        {/* Article Content - Passed as children to ArticleAnnotation */}
                        <div className="modern-card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
                        <Typography variant="h5" style={{ color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '1rem' }}>
                          {article.title}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                          <Chip 
                            label={`Authors: ${article.authors}`}
                            className="btn-modern-secondary"
                            size="small"
                          />
                          <Chip 
                            label={`Year: ${article.year}`}
                            className="btn-modern-secondary"
                            size="small"
                          />
                          <Chip 
                            label={`Journal: ${article.journal}`}
                            className="btn-modern-secondary"
                            size="small"
                          />
                          {article.doi && (
                            <Chip 
                              label={`DOI: ${article.doi}`}
                              className="btn-modern-secondary"
                              size="small"
                            />
                          )}
                        </Box>
                        </div>
                        
                        {article.link && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                              Article Link
                            </Typography>
                            <Button
                              className="btn-modern"
                              startIcon={<OpenInNewIcon />}
                              href={article.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                ...getButtonStyles(false, isDarkMode, 'generic'),
                                marginRight: '1rem',
                                textDecoration: 'none'
                              }}
                            >
                              Open Article
                            </Button>
                            {article.read ? (
                              <Button
                                className="btn-modern"
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={() => {
                                  console.log('🔄 Article tab: Mark as Unread clicked', {
                                    articleId: article.id,
                                    title: article.title?.substring(0, 50) + '...',
                                    query_index: article.query_index,
                                    article_index: article.article_index,
                                    currentReadStatus: article.read
                                  });
                                  updateReadStatus(article, false);
                                }}
                                sx={getButtonStyles(false, isDarkMode, 'generic')}
                              >
                                Mark as Unread
                              </Button>
                            ) : (
                              <Button
                                className="btn-modern"
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={() => {
                                  console.log('🔄 Article tab: Mark as Read clicked', {
                                    articleId: article.id,
                                    title: article.title?.substring(0, 50) + '...',
                                    query_index: article.query_index,
                                    article_index: article.article_index,
                                    currentReadStatus: article.read
                                  });
                                  updateReadStatus(article, true);
                                }}
                                sx={getButtonStyles(false, isDarkMode, 'generic')}
                              >
                                Mark as Read
                              </Button>
                            )}
                          </Box>
                        )}
                      </ArticleAnnotation>
                    </Box>
                  ))}
                </Box>
              </div>
            </Grid>
          </Grid>
            </Box> {/* End Main Content Area */}
          </>
        )}

        {/* Modify Dialog */}
        <Dialog 
          open={openModifyDialog} 
          onClose={() => setOpenModifyDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'var(--surface-2)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          <DialogTitle sx={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--glass-border)', pb: 2 }}>
            Modify Search Criteria
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Search Criteria"
              value={criteriaToModify.criteria}
              onChange={(e) =>
                setCriteriaToModify((prev) => ({ ...prev, criteria: [e.target.value] }))
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  '& fieldset': {
                    borderColor: 'var(--glass-border)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--neon-cyan)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--neon-cyan)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--text-primary)',
                  '&.Mui-focused': {
                    color: 'var(--neon-cyan)',
                  },
                },
              }}
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                Start Date
              </Typography>
              <DatePicker
                selected={(() => {
                  try {
                    return criteriaToModify.start_date && criteriaToModify.start_date[0] && criteriaToModify.start_date[0] !== '' 
                      ? new Date(criteriaToModify.start_date[0]) 
                      : null;
                  } catch (e) {
                    return null;
                  }
                })()}
                onChange={(date) =>
                  setCriteriaToModify((prev) => ({ ...prev, start_date: [date ? date.toISOString().split('T')[0] : ''] }))
                }
                placeholderText="Select start date"
                className="form-control"
                dateFormat="yyyy-MM-dd"
                isClearable
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={15}
                style={{
                  width: '100%',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  padding: '0.75rem'
                }}
              />
            </Box>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                End Date
              </Typography>
              <DatePicker
                selected={(() => {
                  try {
                    return criteriaToModify.end_date && criteriaToModify.end_date[0] && criteriaToModify.end_date[0] !== '' 
                      ? new Date(criteriaToModify.end_date[0]) 
                      : null;
                  } catch (e) {
                    return null;
                  }
                })()}
                onChange={(date) =>
                  setCriteriaToModify((prev) => ({ ...prev, end_date: [date ? date.toISOString().split('T')[0] : ''] }))
                }
                placeholderText="Select end date"
                className="form-control"
                dateFormat="yyyy-MM-dd"
                isClearable
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={15}
                style={{
                  width: '100%',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  padding: '0.75rem'
                }}
              />
            </Box>
            
            {/* Domain Selection */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControl fullWidth>
                <InputLabel 
                  id="modify-domain-label" 
                  style={{ color: 'var(--text-primary)' }}
                >
                  Research Domain (for AI Filtering)
                </InputLabel>
                <Select
                  labelId="modify-domain-label"
                  value={criteriaToModify.domain?.[0] || ''}
                  onChange={(e) =>
                    setCriteriaToModify((prev) => ({ ...prev, domain: [e.target.value] }))
                  }
                  label="Research Domain (for AI Filtering)"
                  sx={{
                    color: 'var(--text-primary)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--glass-border)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--neon-cyan)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--neon-cyan)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'var(--text-primary)',
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {availableDomains.filter(d => !d.is_custom).map((domain) => (
                    <MenuItem key={domain.key} value={domain.key}>
                      {domain.key} ({domain.name.charAt(0).toUpperCase() + domain.name.slice(1)})
                    </MenuItem>
                  ))}
                  {availableDomains.filter(d => d.is_custom).length > 0 && (
                    <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.75rem', mt: 1 }}>
                      — MY CUSTOM MODELS —
                    </MenuItem>
                  )}
                  {availableDomains.filter(d => d.is_custom).map((domain) => (
                    <MenuItem key={domain.key} value={domain.key}>
                      ⭐ {domain.name} ({domain.annotation_count} annotations)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => setOpenModifyDialog(false)}
              className="btn-modern-secondary"
              sx={{
                padding: '0.75rem 1.5rem !important',
                fontSize: '0.95rem !important',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveModifiedCriteria}
              className="btn-modern-primary"
              sx={{
                padding: '0.75rem 1.5rem !important',
                fontSize: '0.95rem !important',
                fontWeight: 600,
                color: '#ffffff !important',
                '& .MuiSvgIcon-root': {
                  color: '#ffffff !important'
                }
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Query Confirmation Dialog */}
        <Dialog 
          open={deleteQueryDialogOpen} 
          onClose={() => setDeleteQueryDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'var(--surface-2)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
              backdropFilter: 'blur(10px)'
            }
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(5px)'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: 'var(--text-primary)', 
            borderBottom: '1px solid var(--glass-border)',
            pb: 2
          }}>
            <Typography variant="h6" sx={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon style={{ fontSize: '1.2rem', color: '#ff4444' }} />
              Delete Query
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Typography sx={{ color: 'var(--text-primary)', mb: 2 }}>
              Are you sure you want to delete this query?
            </Typography>
            {queryToDelete !== null && searchCriteria[queryToDelete] && (
              <Box sx={{ 
                p: 2, 
                bgcolor: 'rgba(255, 68, 68, 0.1)', 
                borderRadius: 'var(--radius-md)', 
                border: '1px solid rgba(255, 68, 68, 0.3)',
                mb: 2
              }}>
                <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  "{searchCriteria[queryToDelete].criteria}"
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--text-muted)', display: 'block', mt: 1 }}>
                  {searchCriteria[queryToDelete].start_date} - {searchCriteria[queryToDelete].end_date || 'Present'}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" sx={{ color: 'var(--text-muted)', mb: 1 }}>
              This will permanently delete:
            </Typography>
            <Box component="ul" sx={{ color: 'var(--text-muted)', mt: 1, mb: 2 }}>
              <li>The query and all its search results</li>
              <li>Article annotations and categories for this query</li>
              <li>AI filtering results</li>
            </Box>
            <Typography variant="body2" sx={{ color: '#ff4444', fontWeight: 600 }}>
              ⚠️ This action cannot be undone!
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => {
                setDeleteQueryDialogOpen(false);
                setQueryToDelete(null);
              }}
              className="btn-modern-secondary"
              sx={{
                padding: '0.75rem 1.5rem !important',
                fontSize: '0.95rem !important',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDeleteQuery}
              className="btn-delete"
              sx={{
                padding: '0.75rem 1.5rem !important',
                fontSize: '0.95rem !important',
                fontWeight: 600,
                color: '#ffffff !important',
                boxShadow: 'none !important',
                '&:hover': {
                  boxShadow: 'none !important',
                  transform: 'none !important'
                },
                '& .MuiSvgIcon-root': {
                  color: '#ffffff !important'
                }
              }}
            >
              Delete Query
            </Button>
          </DialogActions>
        </Dialog>

        {/* Share Article Dialog */}
        <Dialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'var(--surface-2)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
            }
          }}
        >
          <DialogTitle sx={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--glass-border)' }}>
            Share Article
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {selectedArticle && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)' }}>
                <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 1 }}>
                  Article to Share
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                  {selectedArticle.title || 'No title'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', mt: 1 }}>
                  {selectedArticle.authors} • {selectedArticle.journal} • {selectedArticle.year}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Search Users"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                sx: { color: 'var(--text-primary)' }
              }}
              InputLabelProps={{
                sx: { color: 'var(--text-secondary)' }
              }}
            />

            <Box sx={{ maxHeight: '200px', overflow: 'auto', mb: 2 }}>
              {(() => {
                // If there's a search query, show all matching users
                if (userSearchQuery.trim()) {
                  return users
                    .filter(user => 
                      user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                      user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                    )
                    .map((user) => (
                      <ListItem
                        key={user.id}
                        button
                        onClick={() => setSelectedUser(user)}
                        sx={{
                          bgcolor: selectedUser?.id === user.id ? 'rgba(108, 48, 130, 0.1)' : 'transparent',
                          border: selectedUser?.id === user.id ? '1px solid var(--neon-cyan)' : '1px solid transparent',
                          borderRadius: 'var(--radius-md)',
                          mb: 1,
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                              {user.name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                              {user.email} • {user.role}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ));
                }
                
                // If no search query, show only the last shared user (if exists)
                if (lastSharedUser) {
                  return (
                    <ListItem
                      key={lastSharedUser.id}
                      button
                      onClick={() => setSelectedUser(lastSharedUser)}
                      sx={{
                        bgcolor: selectedUser?.id === lastSharedUser.id ? 'rgba(108, 48, 130, 0.1)' : 'transparent',
                        border: selectedUser?.id === lastSharedUser.id ? '1px solid var(--neon-cyan)' : '1px solid transparent',
                        borderRadius: 'var(--radius-md)',
                        mb: 1,
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                            {lastSharedUser.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                            {lastSharedUser.email} • {lastSharedUser.role} • Last shared with
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                }
                
                // If no last shared user and no search, show message
                return (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                      Start typing to search for users
                    </Typography>
                  </Box>
                );
              })()}
            </Box>

            <TextField
              fullWidth
              label="Message (optional)"
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder="Add a message to accompany the shared article..."
              multiline
              rows={2}
              InputProps={{
                sx: { color: 'var(--text-primary)' }
              }}
              InputLabelProps={{
                sx: { color: 'var(--text-secondary)' }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid var(--glass-border)' }}>
            <Button
              onClick={() => {
                setShareDialogOpen(false);
                setSelectedUser(null);
                setShareMessage('');
                setUserSearchQuery('');
                setSelectedArticle(null);
              }}
              className="btn-modern btn-modern-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleShareToUser}
              disabled={!selectedUser || sending}
              className="btn-modern btn-modern-primary"
            >
              {sending ? 'Sharing...' : 'Share Article'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Styled Snackbar for Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: '100%',
              backgroundColor: snackbar.severity === 'warning' 
                ? 'rgba(255, 152, 0, 0.9)' 
                : snackbar.severity === 'error'
                ? 'rgba(244, 67, 54, 0.9)'
                : snackbar.severity === 'success'
                ? 'rgba(70, 130, 48, 0.9)'
                : 'rgba(108, 48, 130, 0.9)',
              color: '#ffffff',
              border: snackbar.severity === 'warning'
                ? '1px solid rgba(255, 152, 0, 0.5)'
                : snackbar.severity === 'error'
                ? '1px solid rgba(244, 67, 54, 0.5)'
                : snackbar.severity === 'success'
                ? '1px solid rgba(70, 130, 48, 0.5)'
                : '1px solid rgba(108, 48, 130, 0.5)',
              borderRadius: 'var(--radius-md)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              '& .MuiAlert-icon': {
                color: '#ffffff'
              },
              '& .MuiAlert-action': {
                color: '#ffffff',
                '& .MuiIconButton-root': {
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div> {/* End Full Screen Container */}
      
      {/* Create Domain Dialog */}
      <Dialog 
        open={createDomainOpen} 
        onClose={() => setCreateDomainOpen(false)}
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)' }}>
          Create New Research Domain
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="var(--text-muted)" sx={{ mb: 3 }}>
            Create a custom research domain to organize and filter articles using AI.
            After annotating 50+ "yes" articles, a model will be automatically trained.
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="Domain Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newDomainName}
            onChange={(e) => setNewDomainName(e.target.value)}
            placeholder="e.g., Cardiology, Diabetes, Oncology"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'var(--text-primary)',
                '& fieldset': { borderColor: 'var(--glass-border)' },
                '&:hover fieldset': { borderColor: 'var(--neon-cyan)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--neon-cyan)' }
              },
              '& .MuiInputLabel-root': { color: 'var(--text-muted)' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setCreateDomainOpen(false)}
            className="btn-modern-secondary"
            sx={{
              padding: '0.75rem 1.5rem !important',
              fontSize: '0.95rem !important',
              fontWeight: 500,
              border: isDarkMode 
                ? '1px solid rgba(255, 255, 255, 0.3) !important' 
                : '1px solid rgba(0, 0, 0, 0.2) !important',
              '&.MuiButton-root': {
                border: isDarkMode 
                  ? '1px solid rgba(255, 255, 255, 0.3) !important' 
                  : '1px solid rgba(0, 0, 0, 0.2) !important'
              },
              '&:hover': {
                background: 'rgba(108, 48, 130, 0.08)',
                borderColor: '#6c3082 !important'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateDomain}
            className="btn-modern-primary"
            disabled={domainCreationLoading || !newDomainName.trim()}
            sx={{
              padding: '0.75rem 1.5rem !important',
              fontSize: '0.95rem !important',
              fontWeight: 600,
              color: '#ffffff !important',
              '&.Mui-disabled': {
                opacity: 0.5,
                color: '#ffffff !important'
              }
            }}
          >
            {domainCreationLoading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : 'Create Domain'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Domain Confirmation Dialog */}
      <Dialog 
        open={deleteDomainOpen} 
        onClose={() => setDeleteDomainOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(10px)',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'var(--text-primary)', 
          borderBottom: '1px solid var(--glass-border)',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon style={{ fontSize: '1.2rem', color: '#ff4444' }} />
            Delete Custom Domain
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography sx={{ color: 'var(--text-primary)', mb: 2 }}>
            Are you sure you want to delete <strong>"{domainToDelete?.name}"</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-muted)', mb: 1 }}>
            This will permanently delete:
          </Typography>
          <Box component="ul" sx={{ color: 'var(--text-muted)', mt: 1, mb: 2 }}>
            <li>All annotations ({domainToDelete?.annotation_count || 0} annotations)</li>
            <li>The trained model{domainToDelete?.has_model ? '' : ' (if exists)'}</li>
            <li>All associated data</li>
          </Box>
          <Typography variant="body2" sx={{ color: '#ff4444', fontWeight: 600 }}>
            ⚠️ This action cannot be undone!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid var(--glass-border)' }}>
          <Button 
            onClick={() => {
              setDeleteDomainOpen(false);
              setDomainToDelete(null);
            }}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteDomain}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'delete')}
          >
            Delete Domain
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Selector Dialog */}
      <CategorySelector
        open={categoryDialogOpen}
        onClose={handleCloseCategoryDialog}
        article={articleToCategorize}
        categories={categories}
        onCategoryAssigned={handleCategoryAssigned}
        onCreateCategory={handleCreateCategoryFromSidebar}
      />

      {/* Create/Edit Category Dialog */}
      <Dialog 
        open={createCategoryDialogOpen} 
        onClose={() => setCreateCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <CategoryIcon style={{ color: 'var(--neon-cyan)', marginRight: '0.5rem' }} />
            <Typography variant="h6">
              {newCategoryForm.id ? 'Edit Category' : 'Create New Category'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Category Name */}
            <TextField
              data-tutorial="category-name-input"
              label="Category Name"
              fullWidth
              value={newCategoryForm.name}
              onChange={(e) => setNewCategoryForm({ ...newCategoryForm, name: e.target.value })}
              placeholder="e.g., Fever, Pain, Pediatrics"
              autoFocus
            />

            {/* Color Picker */}
            <Box data-tutorial="category-color-picker">
              <Typography variant="subtitle2" gutterBottom>
                Color
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CATEGORY_COLORS.map(color => {
                  const themeColor = getThemeAwareColor(color, isDarkMode, 0);
                  return (
                    <Box
                      key={color}
                      onClick={() => setNewCategoryForm({ ...newCategoryForm, color })}
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: themeColor,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        border: newCategoryForm.color === color ? '3px solid var(--neon-cyan)' : `2px solid ${isDarkMode ? 'var(--border)' : themeColor}`,
                        boxShadow: isDarkMode ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: isDarkMode ? '0 4px 8px rgba(0,0,0,0.2)' : `0 4px 12px ${themeColor}40`,
                          border: `3px solid ${themeColor}`
                        },
                        transition: 'all 0.2s'
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Preview */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 2,
                  py: 0.75,
                  borderRadius: '16px',
                  ...getCategoryStyles(newCategoryForm.color, isDarkMode, false),
                  border: `2px solid ${getThemeAwareColor(newCategoryForm.color, isDarkMode, 0)}`,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: isDarkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <span>{newCategoryForm.name || 'Category Name'}</span>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setCreateCategoryDialogOpen(false)}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
          <Button
            data-tutorial="category-save-button"
            onClick={handleSaveNewCategory}
            className="btn-modern"
            startIcon={<SaveIcon />}
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            {newCategoryForm.id ? 'Save Changes' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default HomePage;
